// @ts-nocheck
/**
 * Course Materials Sidebar Module
 * Handles all sidebar functionality including campus/term selection, course dropdowns, and student ID lookup
 */

/** Persists typed student ID across navigation when session/start does not issue a token (never use ?student_id=). */
const CMRP_STUDENT_ID_PREFILL_KEY = 'cmrp_student_id_prefill';
/** Last student ID from a validated session/check (survives refresh while session is active). */
const CMRP_SESSION_STUDENT_ID_KEY = 'cmrp_student_id_active';
const CMRP_FLOW_STORAGE_KEY = 'cmrp_flow';
/** Which CMRP entry path the user chose before navigation (mirrors main branch URL restore guard). */
const CMRP_INITIAL_FLOW_KEY = 'cmrp_initial_flow';

function setCmrpInitialFlowPreference(flow) {
  if (flow !== 'student-id' && flow !== 'course-builder') return;
  try {
    sessionStorage.setItem(CMRP_INITIAL_FLOW_KEY, flow);
  } catch (e) {
    /* ignore */
  }
}

function readCmrpInitialFlowPreference() {
  try {
    const raw = sessionStorage.getItem(CMRP_INITIAL_FLOW_KEY);
    if (raw === 'student-id' || raw === 'course-builder') return raw;
    return '';
  } catch (e) {
    return '';
  }
}

window.CMRP_INITIAL_FLOW_KEY = CMRP_INITIAL_FLOW_KEY;
window.setCmrpInitialFlowPreference = setCmrpInitialFlowPreference;
window.readCmrpInitialFlowPreference = readCmrpInitialFlowPreference;
window.cmrpPersistFlow = cmrpPersistFlow;

function cmrpNormalizeFlow(rawFlow) {
  const flow = String(rawFlow || '').trim().toLowerCase();
  if (flow === 'student-id' || flow === 'course-builder' || flow === 'sso') return flow;
  return '';
}

function cmrpPersistFlow(flow) {
  const normalized = cmrpNormalizeFlow(flow);
  try {
    if (normalized) {
      sessionStorage.setItem(CMRP_FLOW_STORAGE_KEY, normalized);
    } else {
      sessionStorage.removeItem(CMRP_FLOW_STORAGE_KEY);
    }
  } catch (e) {
    /* ignore storage failures */
  }
  return normalized;
}

function cmrpReadFlowFromStorage() {
  try {
    return cmrpNormalizeFlow(sessionStorage.getItem(CMRP_FLOW_STORAGE_KEY));
  } catch (e) {
    return '';
  }
}

/** Normalize `shopBy` for comparisons (`Course` / `course` / `courseRefId`). */
function cmrpNormalizeShopByParam(raw) {
  try {
    return String(raw != null ? decodeURIComponent(String(raw)) : '')
      .trim()
      .toLowerCase();
  } catch (e) {
    return String(raw || '')
      .trim()
      .toLowerCase();
  }
}

function cmrpReadShopByFromSearch(search = window.location.search) {
  return cmrpNormalizeShopByParam(
    new URLSearchParams(search).get('shopBy'),
  );
}

function cmrpShopByIsStudentId(shopBy) {
  const value = cmrpNormalizeShopByParam(shopBy);
  return value === 'studentid' || value === 'student-id';
}

/**
 * shopBy values that load course-builder / Discover course data (not Student ID).
 * Includes Follett Discover variants: course, courseRefId, discoverview, discovershop, discoveraccess.
 */
function cmrpShopByMeansCourseUrl(shopBy) {
  const value = cmrpNormalizeShopByParam(shopBy);
  if (!value || cmrpShopByIsStudentId(value)) return false;
  return true;
}

function cmrpUrlHasCourseRestoreData(params) {
  const search = params || new URLSearchParams(window.location.search);
  return Boolean(
    search.get('courseRefId') ||
      search.get('courseDisplayName') ||
      search.get('sectionDisplayName') ||
      search.get('departmentDisplayName'),
  );
}

/**
 * URL should restore as course-builder even if sessionStorage still has student-id.
 * Student ID result URLs keep shopBy=studentid and stay on that flow.
 */
function cmrpUrlShouldRestoreAsCourseBuilder(params) {
  const search = params || new URLSearchParams(window.location.search);
  if (cmrpShopByIsStudentId(search.get('shopBy'))) return false;
  return (
    cmrpShopByMeansCourseUrl(search.get('shopBy')) ||
    cmrpUrlHasCourseRestoreData(search)
  );
}

function cmrpApplyCourseBuilderFlowFromUrl() {
  cmrpPersistFlow('course-builder');
  setCmrpInitialFlowPreference('course-builder');
  cmrpApplyFlowChrome('course-builder');
}

/** Map `shopBy` → CMRP flow. Empty when the param is absent or unknown. */
function cmrpFlowFromShopByParam(search = window.location.search) {
  const shopBy = cmrpReadShopByFromSearch(search);
  if (cmrpShopByIsStudentId(shopBy)) return 'student-id';
  if (cmrpShopByMeansCourseUrl(shopBy)) return 'course-builder';
  return '';
}

function cmrpShopByParamForFlow(flow) {
  const normalized = cmrpNormalizeFlow(flow);
  if (normalized === 'student-id' || normalized === 'sso') return 'studentid';
  if (normalized === 'course-builder') return 'course';
  return '';
}

/**
 * Course-builder URL that should override sticky student-id session/flow.
 * Stays student-id only when URL explicitly says `shopBy=studentid`.
 */
function cmrpUrlPrefersCourseBuilderDeepLink(search = window.location.search) {
  return cmrpUrlShouldRestoreAsCourseBuilder(new URLSearchParams(search));
}

function cmrpApplyFlowChrome(flow) {
  const filtersSection = document.getElementById('filters-section');
  if (!filtersSection) return;
  if (flow === 'student-id' || flow === 'sso') {
    filtersSection.classList.add('student-id-mode');
    filtersSection.classList.remove('cmrp-filters-panel--landing-chrome');
    return;
  }
  if (flow === 'course-builder') {
    filtersSection.classList.remove('student-id-mode');
    if (filtersSection.dataset?.cmrpStudentIdActive === 'true') {
      filtersSection.classList.add('cmrp-filters-panel--landing-chrome');
    }
  }
}

/**
 * Adopt CMRP chrome from `shopBy` (and course deep-link params when shopBy is
 * absent).
 * @returns {boolean} true when URL forced a flow
 */
function cmrpAdoptFlowFromShopByIfNeeded() {
  const fromShopBy = cmrpFlowFromShopByParam();
  if (fromShopBy) {
    cmrpPersistFlow(fromShopBy);
    if (fromShopBy === 'student-id') {
      setCmrpInitialFlowPreference('student-id');
    } else {
      setCmrpInitialFlowPreference('course-builder');
    }
    cmrpApplyFlowChrome(fromShopBy);
    return true;
  }
  if (cmrpUrlPrefersCourseBuilderDeepLink()) {
    cmrpPersistFlow('course-builder');
    setCmrpInitialFlowPreference('course-builder');
    cmrpApplyFlowChrome('course-builder');
    return true;
  }
  return false;
}

function cmrpCurrentFlow() {
  const fromShopBy = cmrpFlowFromShopByParam();
  if (fromShopBy) {
    cmrpPersistFlow(fromShopBy);
    return fromShopBy;
  }
  return cmrpReadFlowFromStorage();
}

/** Query keys stripped when switching CMRP flow so the landing reloads without stale results. */
const CMRP_RESULTS_URL_PARAM_KEYS = [
  'shopBy',
  'termId',
  'divisionDisplayName',
  'departmentDisplayName',
  'courseDisplayName',
  'courseRefId',
  'sectionDisplayName',
  'campus',
  'campusId',
  'term',
  'student_id',
  'code',
  'sidError',
];

function cmrpClearResultsSearchParams(params) {
  CMRP_RESULTS_URL_PARAM_KEYS.forEach(key => {
    params.delete(key);
  });
}

/** Navigate to the same CMRP page with course/results deep-link params removed. */
function cmrpNavigateForFlowSwitch(flow) {
  const plan =
    typeof window.CmrpSessionSnapshot?.planFlowEntry === 'function'
      ? window.CmrpSessionSnapshot.planFlowEntry(flow)
      : null;
  // Switching back to the parked flow: go to lastResultsUrl so URL restore runs.
  if (plan?.action === 'restore' && plan.href) {
    window.CmrpSessionSnapshot?.prepareFlowSwitch?.(flow);
    window.location.assign(plan.href);
    return;
  }

  // Park the current snapshot; the destination flow hides it until the user
  // submits results there (or switches back).
  if (typeof window.CmrpSessionSnapshot?.prepareFlowSwitch === 'function') {
    window.CmrpSessionSnapshot.prepareFlowSwitch(flow);
  } else {
    try {
      const shop = String(
        window.Shopify?.shop || window.location.hostname || 'default'
      ).toLowerCase();
      localStorage.setItem(`cmrp_skip_last_url::${shop}`, '1');
    } catch (e) {
      /* ignore */
    }
  }
  const params = new URLSearchParams(window.location.search);
  cmrpClearResultsSearchParams(params);
  const shopBy =
    cmrpShopByParamForFlow(flow) ||
    cmrpShopByParamForFlow(cmrpReadFlowFromStorage());
  if (shopBy) params.set('shopBy', shopBy);
  const qs = params.toString();
  const href = qs
    ? `${window.location.pathname}?${qs}`
    : window.location.pathname;
  window.location.assign(href);
}

function cmrpHasDeepLinkCourseParamsInUrl() {
  if (typeof URLStateManager?.hasDeepLinkCourseParamsInSearch === 'function') {
    return URLStateManager.hasDeepLinkCourseParamsInSearch(window.location.search);
  }
  const p = new URLSearchParams(window.location.search);
  return Boolean(
    p.get('sectionDisplayName') || p.get('courseDisplayName') || p.get('courseRefId')
  );
}

function cmrpIsUrlCourseRestorePending() {
  if (
    typeof window.CmrpSessionSnapshot?.isExpiryPromptPending === 'function' &&
    window.CmrpSessionSnapshot.isExpiryPromptPending()
  ) {
    return true;
  }
  const urlManager = window.courseMaterialsPageInstance?.components?.urlManager;
  if (urlManager && typeof urlManager.isUrlRestorePending === 'function') {
    return urlManager.isUrlRestorePending();
  }
  return cmrpHasDeepLinkCourseParamsInUrl();
}

/**
 * CMRP: course-builder and student-id forms use separate element ids when both render (`cb` / `sid`).
 */
function cmrpPanel() {
  return document.getElementById('filters-section');
}

/**
 * CMRP filter `<select>`: fallback max chars when the control has no usable laid-out width yet.
 */
function cmrpSelectLabelMaxLenViewportFallback() {
  const w =
    typeof window.visualViewport?.width === 'number'
      ? Math.round(window.visualViewport.width)
      : typeof window.innerWidth === 'number'
        ? window.innerWidth
        : 1024;

  if (w <= 390) return 14;
  if (w <= 749) return 20;
  if (w <= 968) return 24;
  if (w <= 1100) return 11;
  if (w <= 1280) return 14;
  if (w <= 1600) return 18;
  return 22;
}

/** @type {CanvasRenderingContext2D | null | undefined} */
let cmrpTruncateMeasureCtx;

function cmrpGetTruncateMeasureCtx() {
  if (cmrpTruncateMeasureCtx === undefined) {
    const canvas =
      typeof document !== 'undefined' ? document.createElement('canvas') : null;
    cmrpTruncateMeasureCtx = canvas ? canvas.getContext('2d') : null;
  }
  return cmrpTruncateMeasureCtx;
}

/**
 * Approximate horizontal space for the closed `<select>` label (padding already reserves icons / arrow).
 * @param {HTMLSelectElement} select
 * @returns {number | null}
 */
/**
 * Subtract from (clientWidth − horizontal padding) for truncate fitting.
 * Native `<select>` text paints a bit wider than canvas measureText; extra slack keeps "…" off the chevron.
 */
const CMRP_SELECT_TRUNCATE_GUTTER_PX = 20;

function cmrpSelectAvailableTextWidthPx(select) {
  if (!select || typeof select.clientWidth !== 'number') return null;
  const cw = select.clientWidth;
  if (cw < 1) return null;
  const cs = getComputedStyle(select);
  const pl = parseFloat(cs.paddingLeft) || 0;
  const pr = parseFloat(cs.paddingRight) || 0;
  return Math.max(12, cw - pl - pr - CMRP_SELECT_TRUNCATE_GUTTER_PX);
}

/**
 * Max code-unit prefix length that fits in the select's text area; includes room for "…" when truncated.
 * @param {HTMLSelectElement} select
 * @param {string} full
 * @returns {number}
 */
function cmrpMeasureTruncatedLabelLength(select, full) {
  const raw = String(full ?? '');
  if (!raw.length) return 0;

  const avail = cmrpSelectAvailableTextWidthPx(select);
  const ctx = cmrpGetTruncateMeasureCtx();

  if (!ctx || avail == null) {
    const cap = cmrpSelectLabelMaxLenViewportFallback();
    return raw.length > cap ? cap : raw.length;
  }

  const cs = getComputedStyle(select);
  ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;

  if (ctx.measureText(raw).width <= avail) {
    return raw.length;
  }

  const ell = '\u2026';
  const wEll = ctx.measureText(ell).width;
  if (wEll >= avail) {
    return 0;
  }

  let lo = 0;
  let hi = raw.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    const w = ctx.measureText(raw.slice(0, mid) + ell).width;
    if (w <= avail) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/**
 * Full label for an `<option>` (stable via `data-cmrp-full-label`).
 * @param {HTMLOptionElement | null | undefined} opt
 * @returns {string}
 */
function cmrpOptionFullLabel(opt) {
  if (!opt) return '';
  const stored = opt.dataset?.cmrpFullLabel;
  if (stored != null && stored !== '') return stored;
  const t = String(opt.textContent ?? opt.text ?? '').trim();
  opt.dataset.cmrpFullLabel = t;
  return t;
}

/**
 * @param {HTMLOptionElement} option
 * @param {string} fullText
 */
function cmrpStampOptionFullLabel(option, fullText) {
  const s = String(fullText ?? '');
  option.dataset.cmrpFullLabel = s;
  option.textContent = s;
}

/**
 * Keep first occurrence per normalized key (dropdown option values / labels).
 * @template T
 * @param {T[]} items
 * @param {(item: T) => string} keyFn
 * @returns {T[]}
 */
function cmrpUniqueByKey(items, keyFn) {
  if (!Array.isArray(items) || !items.length) return [];
  const seen = new Set();
  const out = [];
  for (const item of items) {
    const key = String(keyFn(item) ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '');
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

/**
 * IDs of rows that share the selected row's display label, so a unique option
 * list still loads every child that belongs to a duplicate-named parent.
 * @param {any[]} items
 * @param {string} selectedId
 * @param {(item: any) => string} getId
 * @param {(item: any) => string} getLabel
 * @returns {Set<string>}
 */
function cmrpIdsSharingDisplayLabel(items, selectedId, getId, getLabel) {
  const norm = value =>
    String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '');
  const selectedNormId = norm(selectedId);
  if (!selectedNormId) return new Set();
  const list = Array.isArray(items) ? items : [];
  const selected = list.find(item => norm(getId(item)) === selectedNormId);
  const label = selected ? norm(decodeHtmlEntities(getLabel(selected))) : '';
  if (!label) return new Set([selectedNormId]);
  const ids = new Set();
  for (const item of list) {
    if (norm(decodeHtmlEntities(getLabel(item))) !== label) continue;
    const id = norm(getId(item));
    if (id) ids.add(id);
  }
  return ids.size ? ids : new Set([selectedNormId]);
}

/**
 * @param {HTMLSelectElement | null | undefined} select
 * @returns {string}
 */
function cmrpSelectSelectedFullLabel(select) {
  if (!select || select.selectedIndex < 0) return '';
  const opt = select.options[select.selectedIndex];
  return opt ? cmrpOptionFullLabel(opt) : '';
}

/** Restore full option labels while open; truncate closed-field label with measured ellipsis. */
function cmrpTruncateCmrpFilterSelectSelected(select) {
  if (!select?.options || select.selectedIndex < 0) return;

  if (document.activeElement === select) {
    for (let i = 0; i < select.options.length; i++) {
      const opt = select.options[i];
      const full = cmrpOptionFullLabel(opt);
      if (String(opt.textContent ?? '') !== full) {
        opt.textContent = full;
      }
    }
    return;
  }

  const selIdx = select.selectedIndex;
  let maxLen;
  for (let i = 0; i < select.options.length; i++) {
    const opt = select.options[i];
    const full = cmrpOptionFullLabel(opt);
    if (i === selIdx) {
      maxLen = cmrpMeasureTruncatedLabelLength(select, full);
      const visibleText =
        full.length > maxLen ? `${full.slice(0, maxLen)}\u2026` : full;
      if (String(opt.textContent ?? '') !== visibleText) {
        opt.textContent = visibleText;
      }
    } else {
      if (String(opt.textContent ?? '') !== full) {
        opt.textContent = full;
      }
    }
  }
  const selectedFull = cmrpSelectSelectedFullLabel(select);
  const ml =
    maxLen !== undefined
      ? maxLen
      : cmrpMeasureTruncatedLabelLength(select, selectedFull);
  select.title = selectedFull.length > ml ? selectedFull : '';
}

function cmrpTruncateCmrpFilterSelectsIn(panel) {
  const root = panel || document;
  root
    .querySelectorAll(
      'select.cmrp-cb-select:not(.cmrp-custom-dropdown__native), ' +
        'select.cmrp-student-glass-select:not(.cmrp-custom-dropdown__native)',
    )
    .forEach(sel => {
      cmrpTruncateCmrpFilterSelectSelected(sel);
    });
  cmrpSizeCampusTermFieldsIn(root);
}

/**
 * Measure rendered text width without mutating `<option>` nodes (avoids mutation-observer loops).
 * @param {string} text
 * @param {Element} referenceEl
 * @returns {number}
 */
function cmrpMeasureSelectLabelWidth(text, referenceEl) {
  const style = window.getComputedStyle(referenceEl);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return 0;
  ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  return ctx.measureText(text).width;
}

/** Desktop campus/term field minimum widths (px). */
const CMRP_CAMPUS_FIELD_MIN_WIDTH = 250;
const CMRP_TERM_FIELD_MIN_WIDTH = 100;

/** @param {HTMLSelectElement} select @param {number} floorMin */
function cmrpMeasureCampusTermFieldWidth(select, floorMin) {
  if (!select?.options?.length) return floorMin;

  let maxText = 0;
  for (let i = 0; i < select.options.length; i++) {
    const label = cmrpOptionFullLabel(select.options[i]);
    maxText = Math.max(maxText, cmrpMeasureSelectLabelWidth(label, select));
  }

  const itemChrome = 18 + 8 + 24 + 28;
  return Math.max(floorMin, Math.ceil(maxText) + itemChrome);
}

/** @param {Element} item */
function cmrpCampusTermFieldFloorMin(item) {
  return item.querySelector('.buy-by-course-builder__field--term')
    ? CMRP_TERM_FIELD_MIN_WIDTH
    : CMRP_CAMPUS_FIELD_MIN_WIDTH;
}

/**
 * Match campus + term pill widths in one course-builder row (desktop only).
 * @param {Element | null | undefined} wrapper
 */
function cmrpSizeCampusTermRow(wrapper) {
  if (!wrapper) return;

  const items = [...wrapper.querySelectorAll('.cmrp-cb-item--campus-term')];
  if (!items.length) return;

  // Shared Campus/Term row uses equal flex columns; do not content-size widths.
  const equalWidthRow = Boolean(wrapper.closest('.cmrp-campus-term-row'));
  const isMobile =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 968px)').matches;

  if (isMobile || equalWidthRow) {
    for (const item of items) {
      item.style.removeProperty('--cmrp-cb-min-width');
      item.style.removeProperty('width');
      item.style.removeProperty('min-width');
      item.querySelector('select')?.style.removeProperty('width');
    }
    return;
  }

  for (const item of items) {
    if (item.style.display === 'none') {
      item.style.removeProperty('--cmrp-cb-min-width');
      item.style.removeProperty('width');
      item.style.removeProperty('min-width');
      continue;
    }
    const select = item.querySelector('select.cmrp-cb-select');
    const floorMin = cmrpCampusTermFieldFloorMin(item);
    const contentMinWidth = select
      ? cmrpMeasureCampusTermFieldWidth(select, floorMin)
      : floorMin;
    item.style.setProperty('--cmrp-cb-min-width', `${contentMinWidth}px`);
    item.style.removeProperty('width');
    item.style.removeProperty('min-width');
    select?.style.removeProperty('width');
  }
}

/** @param {ParentNode | Document} [root] */
function cmrpSizeCampusTermFieldsIn(root) {
  const scope = root || document;
  scope
    .querySelectorAll('.cmrp-filter-form--course-builder .cmrp-cb-controls')
    .forEach(wrapper => {
      cmrpSizeCampusTermRow(wrapper);
    });
}

/** @deprecated Use {@link cmrpSizeCampusTermFieldsIn} */
function cmrpSizeCampusSelectToContent(select) {
  const wrapper = select?.closest('.cmrp-cb-controls');
  if (wrapper) cmrpSizeCampusTermRow(wrapper);
}

/** @deprecated Use {@link cmrpSizeCampusTermFieldsIn} */
function cmrpSizeCampusSelectsIn(root) {
  cmrpSizeCampusTermFieldsIn(root);
}

function cmrpAttachCmrpSelectTruncationListeners() {
  const panel = document.getElementById('filters-section');
  if (!panel || panel.dataset.cmrpSelectTruncationBound === '1') return;
  panel.dataset.cmrpSelectTruncationBound = '1';
  const selectMatcher =
    'select.cmrp-cb-select:not(.cmrp-custom-dropdown__native), ' +
    'select.cmrp-student-glass-select:not(.cmrp-custom-dropdown__native)';
  panel.addEventListener('change', e => {
    const t = e.target;
    if (t?.matches?.(selectMatcher)) {
      cmrpTruncateCmrpFilterSelectSelected(t);
      if (t.closest('.cmrp-cb-item--campus-term')) {
        cmrpSizeCampusTermRow(t.closest('.cmrp-cb-controls'));
      }
    }
  });

  window.addEventListener('resize', () => {
    cmrpSizeCampusTermFieldsIn(panel);
  });
}

/** Re-apply titles after async campus/term/DDCS fills `<option>` nodes (batched). */
function cmrpInstallCmrpSelectTruncationMutationObserver() {
  const panel = document.getElementById('filters-section');
  if (!panel || panel.dataset.cmrpSelectTruncationMo === '1') return;
  panel.dataset.cmrpSelectTruncationMo = '1';
  let moTimer;
  const scheduleUpdate = () => {
    clearTimeout(moTimer);
    moTimer = setTimeout(() => {
      window.cmrpTruncateCmrpFilterSelectsIn?.(panel);
    }, 100);
  };
  const mo = new MutationObserver(scheduleUpdate);
  mo.observe(panel, { subtree: true, childList: true });
}

/** Re-truncate when CMRP panel width changes without a window resize (sidebar, glass fit-content, etc.). */
function cmrpInstallCmrpSelectTruncateResizeObserver() {
  const panel = document.getElementById('filters-section');
  if (!panel || panel.dataset.cmrpSelectTruncateRo === '1') return;
  if (typeof ResizeObserver !== 'function') return;
  panel.dataset.cmrpSelectTruncateRo = '1';
  let roTimer;
  const ro = new ResizeObserver(() => {
    clearTimeout(roTimer);
    roTimer = setTimeout(() => {
      window.cmrpTruncateCmrpFilterSelectsIn?.(panel);
    }, 40);
  });
  ro.observe(panel);
}

window.cmrpOptionFullLabel = cmrpOptionFullLabel;
window.cmrpStampOptionFullLabel = cmrpStampOptionFullLabel;
window.cmrpUniqueByKey = cmrpUniqueByKey;
window.cmrpIdsSharingDisplayLabel = cmrpIdsSharingDisplayLabel;
window.cmrpTruncateCmrpFilterSelectsIn = cmrpTruncateCmrpFilterSelectsIn;
window.cmrpSizeCampusTermFieldsIn = cmrpSizeCampusTermFieldsIn;
window.cmrpSizeCampusTermRow = cmrpSizeCampusTermRow;
window.cmrpSizeCampusSelectToContent = cmrpSizeCampusSelectToContent;
window.cmrpSizeCampusSelectsIn = cmrpSizeCampusSelectsIn;
window.cmrpSelectSelectedFullLabel = cmrpSelectSelectedFullLabel;

function cmrpStudentIdThemeEnabled() {
  return cmrpPanel()?.dataset?.cmrpStudentIdActive === 'true';
}

/** True when both CMRP forms are visible and wired as dual rows (legacy). Student ID theme uses one flow at a time via CSS. */
function cmrpShowBothForms() {
  return false;
}

/** When course-builder + student-id both render, sid row must not mutate cb globals or cb DOM. */
function cmrpIsolateStudentIdCampusTermFromCourseBuilder(rowScope) {
  return cmrpShowBothForms() && rowScope === 'sid';
}

/** Division → section + Add button (hidden independently of campus/term when course line is toggled off). */
function cmrpCourseBuilderCourseLineFieldEls() {
  return document.querySelectorAll(
    '.cmrp-filter-form--course-builder .cmrp-course-line-fields',
  );
}

function cmrpIsDesktopCmrpLayout() {
  return window.matchMedia && window.matchMedia('(min-width: 969px)').matches;
}

/** True on CMRP results template (`CourseMaterialsPage` adds `course-materials-page` to body). */
function cmrpIsCourseMaterialsResultsPage() {
  return document.body?.classList?.contains('course-materials-page') === true;
}

/** Remember original `#products-section` position in `.course-materials` for mobile / collapsed sidebar. */
let cmrpProductsDefaultSlot = null;

function rememberCmrpProductsDefaultSlot() {
  const products = document.getElementById('products-section');
  if (!products || cmrpProductsDefaultSlot) return;
  cmrpProductsDefaultSlot = { parent: products.parentNode, next: products.nextSibling };
}

function restoreCmrpProductsToDefaultSlot() {
  const products = document.getElementById('products-section');
  if (!products || !cmrpProductsDefaultSlot?.parent) return;
  const { parent, next } = cmrpProductsDefaultSlot;
  if (next && next.parentNode === parent) {
    parent.insertBefore(products, next);
  } else {
    parent.appendChild(products);
  }
}

/**
 * True when docked `#products-section` targets `.cmrp-results-anchor--student-id`.
 * Same branching as `cmrpResolveProductsAnchorEl` (viewport check excluded). Used by URL restore
 * so only the anchored form reads deep-link params and course-builder chips stay empty.
 * @returns {boolean}
 */
function cmrpResolveProductsAnchorUnderStudentForm() {
  const params = new URLSearchParams(window.location.search);
  const flow = cmrpCurrentFlow();
  const panel = document.getElementById('filters-section');
  const sidMode = panel?.classList.contains('student-id-mode') === true;

  const hasCourseUrlParams = cmrpUrlHasCourseRestoreData(params);
  const shopByParam = params.get('shopBy') || '';
  const shopByMeansCourseBuilder = cmrpShopByMeansCourseUrl(shopByParam);
  const shopByMeansStudentId = cmrpShopByIsStudentId(shopByParam);

  // Explicit student-id deep link / resume always docks under the student-id form,
  // even when the URL also carries course DisplayName params (normal for SID results).
  if (shopByMeansStudentId) {
    return true;
  }

  const page = window.courseMaterialsPageInstance;
  const filters = page?.selectedFilters;
  if (Array.isArray(filters) && filters.length > 0) {
    const last = filters[filters.length - 1];
    // URL shopBy=course always wins over a stale fromStudentId stamp (session restore race).
    if (flow === 'course-builder' || shopByMeansCourseBuilder) {
      return false;
    }
    if (last?.fromStudentId === true) {
      return true;
    }
    if (flow === 'student-id' || flow === 'sso' || shopByMeansStudentId) {
      return true;
    }
    if (cmrpIsCourseMaterialsResultsPage()) {
      return true;
    }
    return false;
  }

  // URL beats session `cmrp_flow`: a prior student-id visit leaves `student-id` in
  // sessionStorage while Discover / share links arrive with shopBy=course, discovershop,
  // discoveraccess, courseRefId, or other course-data params.
  if (cmrpUrlShouldRestoreAsCourseBuilder(params)) {
    return false;
  }

  if (flow === 'course-builder') {
    return false;
  }
  if (flow === 'student-id' || flow === 'sso') {
    return true;
  }

  if (cmrpIsCourseMaterialsResultsPage()) {
    return true;
  }

  if (sidMode) {
    return true;
  }

  const emptyBuilder = !hasCourseUrlParams;
  if (emptyBuilder) {
    return true;
  }
  return false;
}

/**
 * Mount `#products-section` under the course-builder or student-id `.cmrp-results-anchor`
 * (CMRP results: all viewports; other templates: desktop only — see anchorCmrpProductsPanel).
 */
function cmrpResolveProductsAnchorEl() {
  if (!cmrpIsDesktopCmrpLayout() && !cmrpIsCourseMaterialsResultsPage()) return null;

  if (cmrpUrlPrefersCourseBuilderDeepLink()) {
    return document.querySelector('.cmrp-results-anchor--course-builder');
  }
  if (cmrpShopByIsStudentId(cmrpReadShopByFromSearch())) {
    return document.querySelector('.cmrp-results-anchor--student-id');
  }

  if (!cmrpShowBothForms()) {
    if (!cmrpStudentIdThemeEnabled()) {
      return document.querySelector('.cmrp-results-anchor--course-builder');
    }
    return document.querySelector(
      cmrpResolveProductsAnchorUnderStudentForm()
        ? '.cmrp-results-anchor--student-id'
        : '.cmrp-results-anchor--course-builder',
    );
  }

  return document.querySelector(
    cmrpResolveProductsAnchorUnderStudentForm()
      ? '.cmrp-results-anchor--student-id'
      : '.cmrp-results-anchor--course-builder',
  );
}

function anchorCmrpProductsPanel() {
  const products = document.getElementById('products-section');
  if (!products) return;
  const y =
    window.scrollY ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0;
  rememberCmrpProductsDefaultSlot();

  if (!cmrpIsDesktopCmrpLayout() && !cmrpIsCourseMaterialsResultsPage()) {
    restoreCmrpProductsToDefaultSlot();
    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    html.scrollTop = y;
    if (document.body) document.body.scrollTop = y;
    window.scrollTo(0, y);
    html.style.scrollBehavior = prev;
    return;
  }

  const target = cmrpResolveProductsAnchorEl();
  if (target) {
    target.appendChild(products);
  } else {
    restoreCmrpProductsToDefaultSlot();
  }

  if (typeof window.syncCmrpProductsSectionShell === 'function') {
    window.syncCmrpProductsSectionShell();
  }

  const html = document.documentElement;
  const prev = html.style.scrollBehavior;
  html.style.scrollBehavior = 'auto';
  html.scrollTop = y;
  if (document.body) document.body.scrollTop = y;
  window.scrollTo(0, y);
  html.style.scrollBehavior = prev;
}

window.anchorCmrpProductsPanel = anchorCmrpProductsPanel;
window.cmrpResolveProductsAnchorUnderStudentForm =
  cmrpResolveProductsAnchorUnderStudentForm;

if (typeof window !== 'undefined') {
  window.cmrpCurrentFlow = cmrpCurrentFlow;
  window.cmrpPersistFlow = cmrpPersistFlow;
  window.cmrpNormalizeShopByParam = cmrpNormalizeShopByParam;
  window.cmrpReadShopByFromSearch = cmrpReadShopByFromSearch;
  window.cmrpFlowFromShopByParam = cmrpFlowFromShopByParam;
  window.cmrpShopByParamForFlow = cmrpShopByParamForFlow;
  window.cmrpUrlPrefersCourseBuilderDeepLink = cmrpUrlPrefersCourseBuilderDeepLink;
  window.cmrpAdoptFlowFromShopByIfNeeded = cmrpAdoptFlowFromShopByIfNeeded;
  window.cmrpShopByMeansCourseUrl = cmrpShopByMeansCourseUrl;
  window.cmrpShopByIsStudentId = cmrpShopByIsStudentId;
  window.cmrpUrlHasCourseRestoreData = cmrpUrlHasCourseRestoreData;
  window.cmrpUrlShouldRestoreAsCourseBuilder = cmrpUrlShouldRestoreAsCourseBuilder;
}

/** `cb` vs `sid` campus/term row, from a campus or term `<select>` id. */
function cmrpScopeFromCtSelect(el) {
  if (!el?.id) return 'cb';
  if (el.id.endsWith('-sid')) return 'sid';
  return 'cb';
}

/** Course-builder desktop campus/term `<select>` (cb suffix when dual-form). */
function cmrpCourseBuilderCampusTermEl(inner) {
  const cb = cmrpSuffixPair().cb;
  if (cb) return document.getElementById(cmrpId(inner, 'cb'));
  return typeof window.cmrpQueryDesktopCampusTerm === 'function'
    ? window.cmrpQueryDesktopCampusTerm(inner)
    : null;
}

function cmrpSuffixPair() {
  const p = cmrpPanel();
  if (!cmrpStudentIdThemeEnabled()) return { cb: '', sid: '' };
  return {
    cb: (p?.dataset?.cmrpSuffixCourseBuilder || 'cb').trim(),
    sid: (p?.dataset?.cmrpSuffixStudentId || 'sid').trim(),
  };
}

function cmrpActiveCampusTermSuffix() {
  const { cb, sid } = cmrpSuffixPair();
  if (!cb && !sid) return '';
  const panel = cmrpPanel();
  const params = new URLSearchParams(window.location.search);
  const flow = cmrpCurrentFlow();
  if (
    flow === 'student-id' ||
    flow === 'sso' ||
    panel?.classList.contains('student-id-mode')
  ) {
    return sid || cb;
  }
  if (flow === 'course-builder') {
    return cb || sid;
  }
  if (
    cmrpStudentIdThemeEnabled() &&
    !flow &&
    !params.get('sectionDisplayName') &&
    !params.get('courseDisplayName') &&
    !params.get('departmentDisplayName')
  ) {
    return sid || cb;
  }
  return cb || sid;
}

/**
 * @param {'cb' | 'sid' | 'activeCt'} scope activeCt = campus/term row for the active flow
 */
function cmrpId(base, scope) {
  const { cb, sid } = cmrpSuffixPair();
  let suf = '';
  if (scope === 'cb') suf = cb;
  else if (scope === 'sid') suf = sid;
  else if (scope === 'activeCt') suf = cmrpActiveCampusTermSuffix();
  return suf ? `${base}-${suf}` : base;
}

/**
 * Resolve store-level division usage from getFilteredCampusTerm payload.
 * Prefer an explicit top-level boolean when present; otherwise use the first campus.
 * @param {boolean|object|null|undefined} input
 * @returns {{ resolved: boolean, source: string }}
 */
function cmrpResolveDivisionUsedFlagFromCampusTermData(input) {
  if (typeof input === 'boolean') {
    return { resolved: input, source: 'explicit-boolean' };
  }

  const payload =
    input && typeof input === 'object'
      ? input.data && typeof input.data === 'object'
        ? input.data
        : input
      : null;

  if (payload && typeof payload.is_division_used_flag === 'boolean') {
    return {
      resolved: payload.is_division_used_flag,
      source: 'top-level',
    };
  }

  const campuses = Array.isArray(payload?.ddcs_campus)
    ? payload.ddcs_campus
    : [];
  const firstCampus = campuses[0];
  if (firstCampus && typeof firstCampus.is_division_used_flag === 'boolean') {
    return {
      resolved: firstCampus.is_division_used_flag,
      source: 'first-campus',
    };
  }

  return { resolved: false, source: 'fallback-false' };
}

/**
 * Global division flag from getFilteredCampusTerm.
 * Accepts an explicit boolean, or the campus/term payload object.
 * When false, no campus in the store uses divisions — hide division_field everywhere.
 */
function cmrpSetGlobalDivisionUsedFlag(flagOrPayload) {
  const { resolved } =
    cmrpResolveDivisionUsedFlagFromCampusTermData(flagOrPayload);
  window.__cmrpIsDivisionUsedFlag = resolved;
  if (window.courseData && typeof window.courseData === 'object') {
    window.courseData.is_division_used_flag = window.__cmrpIsDivisionUsedFlag;
  }
}

function cmrpIsDivisionUsedGlobally() {
  if (typeof window.__cmrpIsDivisionUsedFlag === 'boolean') {
    return window.__cmrpIsDivisionUsedFlag;
  }
  const fromCourseData = window.courseData?.is_division_used_flag;
  if (typeof fromCourseData === 'boolean') {
    return fromCourseData;
  }
  return true;
}

function cmrpResolveShowDivision(perCampusFlag) {
  const globalFlag = cmrpIsDivisionUsedGlobally();
  return globalFlag && Boolean(perCampusFlag);
}

function cmrpApplyGlobalDivisionFieldVisibility() {
  const globalFlag = cmrpIsDivisionUsedGlobally();
  if (globalFlag) return;
  const divisionField = document.getElementById(cmrpId('division_field', 'cb'));
  const divisionSelect = document.getElementById(cmrpId('division', 'cb'));
  if (divisionField) {
    divisionField.hidden = true;
    divisionField.style.display = 'none';
  }
  if (divisionSelect) {
    divisionSelect.innerHTML = '<option value="">Division</option>';
    divisionSelect.disabled = true;
  }
  window.show_division = false;
  if (window.courseMaterialsSidebar) {
    window.courseMaterialsSidebar.show_division = false;
  }
}

function cmrpCampusTermRootId(scope = 'activeCt') {
  const suf =
    scope === 'activeCt'
      ? cmrpActiveCampusTermSuffix()
      : scope === 'cb'
        ? cmrpSuffixPair().cb
        : cmrpSuffixPair().sid;
  return suf ? `campus-term-dropdowns-${suf}` : 'campus-term-dropdowns';
}

/**
 * Student ID: `#campus-term-dropdowns-{sid}` is on `.cmrp-student-inline-row__inner` (flat layout).
 * Toggle visibility on campus/term `.form-group` children only — not the whole row.
 * Course builder keeps a dedicated wrapper; that element still uses display flex/none on the root.
 * @param {HTMLElement | null} rootEl
 * @param {'show' | 'hide'} mode
 */
function cmrpSetCampusTermDropdownContainerVisibility(rootEl, mode) {
  if (!rootEl) return;
  if (rootEl.classList?.contains('cmrp-student-inline-row__inner')) {
    const formGroups = rootEl.querySelectorAll(':scope > .form-group');
    const singleCampusUi = rootEl.dataset.cmrpCampusUi === 'single';
    formGroups.forEach((el, idx) => {
        if (mode === 'show') {
        if (singleCampusUi && idx === 0) {
          // Let CSS ([data-cmrp-campus-ui="single"]) collapse instantly; avoid display:none so Term/Student ID keep their final flex layout.
          el.style.removeProperty('display');
          el.style.removeProperty('visibility');
          return;
        }
        el.style.display = '';
        el.style.visibility = 'visible';
      } else {
        el.style.display = 'none';
      }
    });
  } else if (mode === 'show') {
    rootEl.style.display = 'flex';
    rootEl.style.visibility = 'visible';
  } else {
    rootEl.style.display = 'none';
  }
}

function cmrpMirrorCampusTermBetweenForms() {
  if (cmrpShowBothForms()) return;
  const { cb, sid } = cmrpSuffixPair();
  if (!cb || !sid) return;
  const a = cmrpActiveCampusTermSuffix();
  const b = a === cb ? sid : cb;
  const pairs = [
    ['campus', 'campus'],
    ['term', 'term'],
  ];
  pairs.forEach(([key]) => {
    const src = document.querySelector(`#campus-term-dropdowns-${a} #${key}-${a}`);
    const dst = document.querySelector(`#campus-term-dropdowns-${b} #${key}-${b}`);
    if (src && dst && src !== dst) {
      dst.innerHTML = src.innerHTML;
      dst.disabled = src.disabled;
      dst.value = src.value;
    }
  });
}

window.cmrpId = cmrpId;
window.cmrpSetGlobalDivisionUsedFlag = cmrpSetGlobalDivisionUsedFlag;
window.cmrpIsDivisionUsedGlobally = cmrpIsDivisionUsedGlobally;
window.cmrpResolveShowDivision = cmrpResolveShowDivision;
window.cmrpApplyGlobalDivisionFieldVisibility = cmrpApplyGlobalDivisionFieldVisibility;
/** Course-builder campus/term `<select>` (cb row when dual-form; canonical when single-form). */
window.cmrpCourseBuilderCampusTermEl = cmrpCourseBuilderCampusTermEl;
window.cmrpQueryDesktopCampusTerm = function cmrpQueryDesktopCampusTerm(inner) {
  const root = cmrpCampusTermRootId('activeCt');
  const id = cmrpId(inner, 'activeCt');
  return document.querySelector(`#${root} #${id}`);
};

/**
 * Defer `#products-section` anchoring until after layout paint (no viewport scroll).
 */
function scheduleCmrpAnchorProductsPanel() {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => anchorCmrpProductsPanel());
  });
}

function cmrpStudentIdLabel() {
  if (typeof window.getCmrpStudentIdLabel === 'function') {
    return window.getCmrpStudentIdLabel();
  }
  const s = window.shopifySettings?.cmrpFlowSwitcherStudentPlaceholder;
  const t = s != null ? String(s).trim() : '';
  return t || 'Student ID';
}

/**
 * Shopify metaobject / JSON values may include HTML entities (e.g. &amp;). Decode for native selects and labels.
 */
function decodeHtmlEntities(text) {
  if (text == null || text === '') return text ?? '';
  const str = String(text);
  if (!str.includes('&')) return str;
  const ta = document.createElement('textarea');
  ta.innerHTML = str;
  return ta.value;
}

if (typeof window !== 'undefined') {
  window.decodeCmrpHtmlEntities = decodeHtmlEntities;
}

/** Lowercase, strip spaces, after decoding entities — for matching API names to UI values. */
function normalizeDdcsLabel(value) {
  return String(decodeHtmlEntities(value ?? ''))
    .toLowerCase()
    .replace(/\s+/g, '');
}

function cmrpHasOAuthCodeInUrl() {
  try {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('code')) return true;
    const h = window.location.hash || '';
    if (!h || h === '#') return false;
    const hp = new URLSearchParams(h.charAt(0) === '#' ? h.slice(1) : h);
    return Boolean(hp.get('code'));
  } catch (e) {
    return false;
  }
}

function cmrpFiltersPanelSsoActive(filtersPanel) {
  if (!filtersPanel?.dataset) return false;
  const ds = filtersPanel.dataset;
  const hasAuth =
    String(ds.ssoAuthUrl || '').trim() !== '' &&
    String(ds.ssoClientId || '').trim() !== '';
  if (!hasAuth) return false;
  if (ds.cmrpTypedStudentIdAllowed === 'false') return true;
  const enabled = String(ds.ssoEnabled || '').toLowerCase();
  return enabled === 'true' || enabled === '1';
}

if (typeof window !== 'undefined') {
  window.cmrpFiltersPanelSsoActive = cmrpFiltersPanelSsoActive;
}

/** Hide typed student ID in the student-id glass row when CMRP SSO is configured. */
function syncCmrpTypedStudentIdDomFlags() {
  const filters = document.getElementById('filters-section');
  const studentFlowOn = cmrpStudentIdThemeEnabled();
  const typedAllowed =
    studentFlowOn && filters && !cmrpFiltersPanelSsoActive(filters);

  if (!filters) return;
  if (!studentFlowOn) {
    delete filters.dataset.cmrpTypedStudentIdAllowed;
    filters.classList.remove('cmrp-sso-no-typed-student-id');
    return;
  }
  filters.dataset.cmrpTypedStudentIdAllowed = typedAllowed ? 'true' : 'false';
  if (typedAllowed) {
    filters.classList.remove('cmrp-sso-no-typed-student-id');
  } else {
    filters.classList.add('cmrp-sso-no-typed-student-id');
  }

  const typedSection = document.querySelector(
    '.cmrp-filter-form--student-id .student-id-section',
  );
  if (typedSection) {
    applyCmrpTypedStudentIdSectionVisibility(typedSection, {
      dualForms: cmrpShowBothForms(),
    });
  }
}

/** Inline `display` on the typed-ID row wins over CSS — keep it off when CMRP SSO is on. */
function applyCmrpTypedStudentIdSectionVisibility(section, { dualForms = false } = {}) {
  if (!section) return;
  const filters = document.getElementById('filters-section');
  if (cmrpFiltersPanelSsoActive(filters)) {
    section.hidden = true;
    section.style.setProperty('display', 'none', 'important');
    return;
  }
  section.hidden = false;
  section.style.removeProperty('display');
  if (!dualForms) {
    section.style.display = 'none';
  }
}

function cmrpReadInitialFlowPreferenceSafe() {
  if (typeof window.readCmrpInitialFlowPreference === 'function') {
    const v = window.readCmrpInitialFlowPreference();
    if (v === 'student-id' || v === 'course-builder') return v;
  }
  try {
    const raw = sessionStorage.getItem(CMRP_INITIAL_FLOW_KEY);
    if (raw === 'student-id' || raw === 'course-builder') return raw;
  } catch (e) {
    /* ignore */
  }
  return '';
}

/**
 * Student-id entry + SSO configured + no session + not mid-OAuth.
 * Revamped CMRP: applies to the student-id glass form (course-builder stays available).
 */
function cmrpShouldShowSsoFirstLanding() {
  if (!cmrpStudentIdThemeEnabled()) return false;
  if (cmrpHasOAuthCodeInUrl()) return false;
  if (cmrpUrlShouldRestoreAsCourseBuilder()) return false;

  const pref = cmrpReadInitialFlowPreferenceSafe();
  const flow = cmrpCurrentFlow();
  const onStudentPath =
    pref === 'student-id' ||
    flow === 'student-id' ||
    flow === 'sso' ||
    document.getElementById('filters-section')?.classList.contains('student-id-mode') ===
      true;

  if (!onStudentPath) return false;

  const filtersPanel = document.getElementById('filters-section');
  if (!cmrpFiltersPanelSsoActive(filtersPanel)) return false;

  try {
    if (sessionStorage.getItem('sessionToken')) return false;
    if (sessionStorage.getItem('cmrp_sso_prefill')) return false;
    if (sessionStorage.getItem(CMRP_SESSION_STUDENT_ID_KEY)) return false;
  } catch (e) {
    return false;
  }
  return true;
}

const CMRP_SSO_LANDING_HINT_HTML =
  '<p class="cmrp-sso-landing-panel__hint">Use your school account to load your courses.</p>';

function cmrpGetSsoLandingBtnFragmentHtml(fragment) {
  const tpl = document.getElementById('cmrp-sso-landing-btn-fragments');
  if (!tpl?.content) return '';
  const node = tpl.content.querySelector(`[data-cmrp-sso-fragment="${fragment}"]`);
  return node ? node.outerHTML : '';
}

function cmrpBuildSsoLandingGlassHtml(filtersDataset) {
  const ds = filtersDataset || {};
  const esc = s =>
    String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;');

  const btnLabel =
    String(ds.ssoCmrpButtonText || '').trim() ||
    (typeof window.shopifySettings?.ssoCmrpButtonText === 'string'
      ? window.shopifySettings.ssoCmrpButtonText.trim()
      : '') ||
    'STUDENT LOGIN';

  return `
      <div class="cmrp-sso-landing-panel__inner">
        <button
          type="button"
          class="btn-primary cmrp-student-shop-btn cmrp-sso-landing-panel__btn"
          data-sso-button="true"
          data-auth-url="${esc(ds.ssoAuthUrl || '')}"
          data-client-id="${esc(ds.ssoClientId || '')}"
          data-idp="${esc(ds.ssoIdp || '')}"
          data-response-type="${esc(ds.ssoResponseType || '')}"
          data-scopes="${esc(ds.ssoScopes || '')}"
          data-state="${esc(ds.ssoState || '')}"
          data-redirect-uri="${esc(ds.ssoRedirectUri || '')}"
        >
          ${cmrpGetSsoLandingBtnFragmentHtml('badge')}
          <span class="cmrp-student-shop-btn__text">${esc(btnLabel)}</span>
          ${cmrpGetSsoLandingBtnFragmentHtml('arrow')}
        </button>
      </div>`;
}

class CourseMaterialsSidebar {
  constructor(config = {}) {
    this.config = {
      ddcs_campus_entity: config.ddcs_campus_entity || [],
      ddcs_campus_division: config.ddcs_campus_division || [],
      ddcs_term: config.ddcs_term || [],
      ddcs_campus_department: config.ddcs_campus_department || [],
      ddcs_course: config.ddcs_course || [],
      ddcs_section: config.ddcs_section || [],
      selectedFilters: config.selectedFilters || [],
      container: config.container || document,
      ...config,
    };

    this.campus_entity_id = '';
    this.term_id = '';
    this.campus_department_id = '';
    this.course_id = '';
    this.division_id = '';
    this.show_division = false;
    this.previousCampusValue = '';
    this.isEditingCampusTerm = false;

    this.init();
  }

  init() {
    this.bindEvents();
    this.initializeSkipSection();
    this.hideDivisionField('sidebar.init');
    this.populateCampus();
    this.initializeAllCoursesList();
    this.initializeMobileTrigger();
    this._scheduleQueryCampusTermHydration();
    this.initializeMobileDrawerOnLoad();
    window.courseMaterialsSidebar = this;

    syncCmrpTypedStudentIdDomFlags();
    const runSyncLanding = () => this.syncCmrpSsoLandingPanelFromSession();
    cmrpRecoverSessionTokenFromCart().finally(() => {
      queueMicrotask(runSyncLanding);
      setTimeout(runSyncLanding, 0);
      setTimeout(runSyncLanding, 150);
    });

    if (!window.__cmrpCampusReadySsoLandingRelayout) {
      window.__cmrpCampusReadySsoLandingRelayout = true;
      document.addEventListener('cmrp:campusTermDataReady', () => {
        window.syncCmrpSsoLandingPanelFromSession?.();
        this.updateCampusTermUnavailableMessage();
      });
    }
  }

  reassertCampusTermUiAfterSsoLandingClear() {
    const filteredCampus = (this.config?.ddcs_campus_entity || []).filter(
      c => !c.disabled_flag,
    );
    this.maybeMarkCourseDetailsSectionCampusUiReady(filteredCampus.length);
  }

  syncCmrpSsoLandingPanelFromSession() {
    const filters = document.getElementById('filters-section');
    const hintMount = document.getElementById('cmrp-sso-landing-hint');
    const glassPanel = document.getElementById('cmrp-sso-landing-panel');
    const show = cmrpShouldShowSsoFirstLanding();

    const clearSsoLandingDom = () => {
      if (hintMount) {
        hintMount.innerHTML = '';
        hintMount.hidden = true;
      }
      if (glassPanel) {
        glassPanel.innerHTML = '';
        glassPanel.hidden = true;
      }
    };

    if (!show) {
      const hadSsoLanding = filters?.classList.contains('cmrp-sso-landing-active') === true;
      filters?.classList.remove('cmrp-sso-landing-active');
      clearSsoLandingDom();
      if (hadSsoLanding) {
        this.reassertCampusTermUiAfterSsoLandingClear();
      }
      window.applyCmrpCourseDetailsSectionFlowUi?.();
      syncCmrpTypedStudentIdDomFlags();
      return;
    }

    filters?.classList.add('cmrp-sso-landing-active');

    const glassHtml = cmrpBuildSsoLandingGlassHtml(filters?.dataset);
    const persistStudentFlow = () => {
      if (typeof window.setCmrpInitialFlowPreference === 'function') {
        window.setCmrpInitialFlowPreference('student-id');
      }
      cmrpPersistFlow('student-id');
    };

    if (hintMount) {
      hintMount.innerHTML = CMRP_SSO_LANDING_HINT_HTML;
      hintMount.hidden = false;
    }

    if (glassPanel) {
      glassPanel.innerHTML = glassHtml;
      glassPanel.hidden = false;
      glassPanel
        .querySelector('[data-sso-button="true"]')
        ?.addEventListener('click', persistStudentFlow, true);
      document.dispatchEvent(new CustomEvent('sso:bindButtons'));
    }

    window.applyCmrpCourseDetailsSectionFlowUi?.();
    syncCmrpTypedStudentIdDomFlags();
  }

  getElementById(id) {
    // Canonical DDCS selects: desktop row for the active CMRP flow (see cmrp-filter-form suffixes).
    if (id === 'campus' || id === 'term') {
      const root = cmrpCampusTermRootId('activeCt');
      const elId = cmrpId(id, 'activeCt');
      const canonical = document.querySelector(`#${root} #${elId}`);
      if (canonical) return canonical;
    }
    const cbOnly = new Set([
      'division',
      'department',
      'course',
      'section',
      'division_field',
      'add-course',
      'selected-courses-container',
      'cmrp-course-details-heading',
    ]);
    if (cbOnly.has(id)) {
      const el = document.getElementById(cmrpId(id, 'cb'));
      if (el) return el;
    }
    const sidOnly = new Set([
      'student-id',
      'submit-student-id',
      'student-id-info-desktop',
      'student-id-submit-container-desktop',
    ]);
    if (sidOnly.has(id)) {
      const el = document.getElementById(cmrpId(id, 'sid'));
      if (el) return el;
    }
    if (id === 'selected-campus-term-text') {
      const el = document.getElementById(cmrpId('selected-campus-term-text', 'activeCt'));
      if (el) return el;
    }
    if (this.config.container && this.config.container !== document) {
      return this.config.container.querySelector(`#${id}`);
    }
    return document.getElementById(id);
  }

  querySelector(selector) {
    return this.config.container.querySelector(selector);
  }

  querySelectorAll(selector) {
    return this.config.container.querySelectorAll(selector);
  }

  /**
   * Refresh mobile drawer campus/term labels and submit state after <select> options change.
   */
  syncMobileCampusTermDisplays() {
    if (!window.courseMaterialsMobile) return;
    const m = window.courseMaterialsMobile;
    if (typeof m.updateCampusDisplay === 'function') {
      m.updateCampusDisplay();
    }
    if (typeof m.updateTermDisplay === 'function') {
      m.updateTermDisplay();
    }
    if (
      m.currentMode === 'studentid' &&
      typeof m.updateMobileSubmitButtonStateForStudentId === 'function'
    ) {
      m.updateMobileSubmitButtonStateForStudentId();
    } else if (typeof m.updateMobileSubmitButtonState === 'function') {
      m.updateMobileSubmitButtonState();
    }
  }

  /**
   * True when we're in shop-by-student-id flow (URL param or UI mode).
   * Used to avoid clearing results/URL when populateTerms/populateDepartmentsFromTerms run.
   */
  isStudentIdMode() {
    const f = cmrpCurrentFlow();
    if (f === 'student-id' || f === 'sso') return true;
    const filtersPanel = document.getElementById('filters-section');
    return filtersPanel?.classList.contains('student-id-mode') === true;
  }

  bindEvents() {
    this.config.container
      .getElementById?.('open-filters')
      ?.addEventListener('click', e => {
        e.preventDefault();
        this.openSidePanel();
      }) ||
      document.getElementById('open-filters')?.addEventListener('click', e => {
        e.preventDefault();
        this.openSidePanel();
      });

    this.config.container
      .getElementById?.('close-filters')
      ?.addEventListener('click', e => {
        e.preventDefault();
        this.closeSidePanel();
      }) ||
      document.getElementById('close-filters')?.addEventListener('click', e => {
        e.preventDefault();
        this.closeSidePanel();
      });

    const cbForm = document.querySelector('.cmrp-filter-form--course-builder');
    if (cbForm) {
      cbForm.addEventListener('change', e => {
        if (e.target instanceof HTMLSelectElement) {
          this.updateAddCourseButtonState();
          window.CmrpCourseRowManager?.updateViewMaterialsButton?.();
        }
      });
    }

    // Multi-row builder: View Materials is owned by CmrpCourseRowManager.
    // Legacy single-row handleAddCourse remains for programmatic / discover callers.
    if (!window.CmrpCourseRowManager) {
      this.getElementById('add-course')?.addEventListener('click', async () =>
        await this.handleAddCourse()
      );
    } else {
      window.CmrpCourseRowManager.init?.();
    }
    // Removed add-student event listener
    this.getElementById('submit-student-id')?.addEventListener('click', () =>
      this.handleAddStudent()
    );
    const studentIdField = this.getElementById('student-id');
    if (studentIdField) {
      // Remove the automatic input event listener to prevent unwanted search logic
      // Student ID will only be processed when SUBMIT button is clicked
    }
  }

  initializeSkipSection() {
    let retryCount = 0;
    const maxRetries = 10;

    const initSkip = () => {
      const skipText = document.querySelector('.skip-text');
      const skipOptions = document.querySelector('.skip-options');
      const chevronIcon = document.querySelector('.chevron-down-icon');

      if (!skipText || !skipOptions || !chevronIcon) {
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(initSkip, 200);
        }
        return;
      }

      chevronIcon.style.transform = 'rotate(180deg)';
      skipOptions.style.display = 'none';

      skipText.onclick = function () {
        const currentSkipOptions = document.querySelector('.skip-options');
        const currentChevronIcon = document.querySelector('.chevron-down-icon');

        if (currentSkipOptions && currentChevronIcon) {
          const isHidden =
            currentSkipOptions.style.display === 'none' ||
            getComputedStyle(currentSkipOptions).display === 'none';

          if (isHidden) {
            currentSkipOptions.style.display = 'block';
            currentChevronIcon.style.transform = 'rotate(0deg)';
          } else {
            currentSkipOptions.style.display = 'none';
            currentChevronIcon.style.transform = 'rotate(180deg)';
          }
        }
      };
    };

    initSkip();
    
    // Initialize panel button states
    this.initializePanelButtons();
  }

  // Initialize panel button states
  initializePanelButtons() {
    const openFiltersBtn = document.getElementById('open-filters');
    const closeFiltersBtn = document.getElementById('close-filters');
    
    // Initially, sidebar is closed, so show open button and hide close button
    if (openFiltersBtn) openFiltersBtn.style.display = 'none';
    if (closeFiltersBtn) closeFiltersBtn.style.display = 'none';
  }

  // Panel Controls
  openSidePanel() {
    const filtersSection = document.getElementById('filters-section');
    const separatorLine = document.getElementById('separator-line');
    const openFiltersBtn = document.getElementById('open-filters');
    const closeFiltersBtn = document.getElementById('close-filters');

    if (filtersSection) filtersSection.style.display = 'block';
    if (separatorLine) separatorLine.style.display = 'block';
    if (openFiltersBtn) openFiltersBtn.style.display = 'none';
    if (closeFiltersBtn) closeFiltersBtn.style.display = 'none';
    
    document.body.classList.add('sidebar-open');
    // Defer reparent so synchronous move while #loading-state is visible does not
    // trigger browser scroll anchoring (viewport jumps toward the footer).
    scheduleCmrpAnchorProductsPanel();
  }

  closeSidePanel() {
    const filtersSection = document.getElementById('filters-section');
    const separatorLine = document.getElementById('separator-line');
    const openFiltersBtn = document.getElementById('open-filters');
    const closeFiltersBtn = document.getElementById('close-filters');

    restoreCmrpProductsToDefaultSlot();
    if (filtersSection) filtersSection.style.display = 'none';
    if (separatorLine) separatorLine.style.display = 'none';
    if (openFiltersBtn) openFiltersBtn.style.display = 'block';
    if (closeFiltersBtn) closeFiltersBtn.style.display = 'none';
    
    document.body.classList.remove('sidebar-open');

    // Finish campus/term editing when closing the panel
    this.finishCampusTermEdit();
  }

  /**
   * Duplicate id="campus" / id="term" exist (filters panel + mobile drawer). Desktop row is canonical;
   * mirror options and values so mobile hidden selects and pickers stay in sync.
   */
  syncMobileDrawerCampusTermSelects() {
    // Both forms use different desktop rows; copying "activeCt" would cross-wire them.
    if (cmrpShowBothForms()) return;

    const deskCampus = cmrpQueryDesktopCampusTerm('campus');
    const mobCampus = document.querySelector('#mobile-campus-term-dropdowns #campus');
    if (deskCampus && mobCampus && deskCampus !== mobCampus) {
      mobCampus.innerHTML = deskCampus.innerHTML;
      mobCampus.disabled = deskCampus.disabled;
      mobCampus.value = deskCampus.value;
    }
    const deskTerm = cmrpQueryDesktopCampusTerm('term');
    const mobTerm = document.querySelector('#mobile-campus-term-dropdowns #term');
    if (deskTerm && mobTerm && deskTerm !== mobTerm) {
      mobTerm.innerHTML = deskTerm.innerHTML;
      mobTerm.disabled = deskTerm.disabled;
      mobTerm.value = deskTerm.value;
    }
  }

  // Campus and Term Management

  getActiveCampuses() {
    return (this.config.ddcs_campus_entity || []).filter(
      campus => !campus.disabled_flag,
    );
  }

  getTermsForCampusEntityId(campusEntityId) {
    const normalize = value =>
      String(value ?? '')
        .toLowerCase()
        .replace(/\s+/g, '');
    const terms = window.courseData?.ddcs_term || this.config.ddcs_term || [];
    return terms.filter(
      term => normalize(term.campus_entity_id) === normalize(campusEntityId),
    );
  }

  /**
   * True when campus/term API has merged and no selectable campus or term options exist for the store.
   */
  isCampusTermSelectionUnavailable() {
    if (window.__cmrpCampusTermFetchApplied !== true) return false;

    const campuses = this.getActiveCampuses();
    if (campuses.length === 0) return true;

    return !campuses.some(
      campus =>
        this.getTermsForCampusEntityId(campus.campus_entity_id).length > 0,
    );
  }

  _cmrpCampusTermUnavailableRowScope(el) {
    const raw = String(el?.dataset?.cmrpCtRowScope ?? '').trim();
    if (raw === 'cb' || raw === 'sid') return raw;
    return 'activeCt';
  }

  applyCampusTermNoDataLabels(noData) {
    const noDataLabel = 'No data';
    const campusNoData = noData && this.getActiveCampuses().length === 0;

    const setSelectNoData = (select, useNoData, defaultPlaceholder) => {
      if (!select) return;
      if (useNoData) {
        select.innerHTML = `<option value="">${noDataLabel}</option>`;
        select.value = '';
        select.disabled = false;
        return;
      }
      if (
        select.options.length === 1 &&
        select.options[0].value === '' &&
        select.options[0].textContent === noDataLabel
      ) {
        select.innerHTML = `<option value="">${defaultPlaceholder}</option>`;
        select.value = '';
      }
    };

    document.querySelectorAll('select[data-cmrp-ct-suffix]').forEach(select => {
      const id = select.id || '';
      const isCampus =
        id === 'campus' || id === cmrpId('campus', 'cb') || id === cmrpId('campus', 'sid');
      const isTerm =
        id === 'term' || id === cmrpId('term', 'cb') || id === cmrpId('term', 'sid');
      if (isCampus) {
        setSelectNoData(select, campusNoData, 'Campus');
      } else if (isTerm) {
        setSelectNoData(select, noData, 'Term');
      }
    });

    document
      .querySelectorAll(
        '#mobile-campus-term-dropdowns #campus, #mobile-campus-term-dropdowns #term',
      )
      .forEach(select => {
        const isCampus = select.id === 'campus';
        setSelectNoData(
          select,
          isCampus ? campusNoData : noData,
          isCampus ? 'Campus' : 'Term',
        );
      });

    const campusDisplay = document.getElementById('campus-display-text');
    const termDisplay = document.getElementById('term-display-text');
    if (campusDisplay) {
      if (campusNoData) {
        campusDisplay.textContent = noDataLabel;
        campusDisplay.setAttribute('data-is-placeholder', 'true');
      } else if (!noData) {
        campusDisplay.textContent = 'Campus';
        campusDisplay.setAttribute('data-is-placeholder', 'true');
      }
    }
    if (termDisplay) {
      if (noData) {
        termDisplay.textContent = noDataLabel;
        termDisplay.setAttribute('data-is-placeholder', 'true');
      } else if (!noData) {
        termDisplay.textContent = 'Term';
        termDisplay.setAttribute('data-is-placeholder', 'true');
      }
    }

    if (!noData) {
      this.syncMobileCampusTermDisplays();
    }
  }

  updateCampusTermUnavailableMessage() {
    const showGlobal = this.isCampusTermSelectionUnavailable();

    document
      .querySelectorAll('.cmrp-campus-term-unavailable-message')
      .forEach(el => {
        let show = showGlobal;
        const rowScope = this._cmrpCampusTermUnavailableRowScope(el);
        const display = document.getElementById(
          cmrpId('campus-term-display', rowScope),
        );
        if (display?.classList.contains('show')) {
          show = false;
        }
        if (show) {
          el.removeAttribute('hidden');
        } else {
          el.setAttribute('hidden', '');
        }
      });

    this.applyCampusTermNoDataLabels(showGlobal);
  }

  /**
   * Desktop: reveal .course-details-section only after campus list is trustworthy.
   * CMRP starts with empty campuses then merges API data — skip until __cmrpCampusTermFetchApplied.
   */
  maybeMarkCourseDetailsSectionCampusUiReady(filteredCampusCount) {
    const fetchApplied = window.__cmrpCampusTermFetchApplied === true;
    if (!fetchApplied && filteredCampusCount === 0) return;
    document
      .querySelectorAll('.course-details-section')
      .forEach(el => el.classList.add('course-details-section--campus-ui-ready'));
  }

  /**
   * Student ID flow: when campus row is hidden (single campus), prompt for term only.
   * @param {boolean} singleCampusMode
   * @param {number} [filteredCampusCount] — used with __cmrpCampusTermFetchApplied so mobile hint stays hidden until CMRP campus API has merged.
   */
  updateStudentIdScheduleHint(singleCampusMode, filteredCampusCount = 0) {
    const sidInfo = document.getElementById(cmrpId('student-id-info-desktop', 'sid'));
    const desktop = sidInfo?.querySelector('.student-id-text');
    const mobile = document.querySelector('#mobile-student-id-section .mobile-student-id-text');
    const mobileInfo = document.querySelector('#mobile-student-id-section .mobile-student-id-info');
    if (desktop) {
      desktop.textContent = singleCampusMode
        ? 'Select Term to pull your schedule'
        : 'Select Campus and Term to pull your schedule';
    }
    if (mobile) {
      mobile.textContent = singleCampusMode
        ? 'Select Term to pull your schedule'
        : 'Enter your Course and Term to pull in your schedule';
    }
    const fetchApplied = window.__cmrpCampusTermFetchApplied === true;
    if (fetchApplied || filteredCampusCount > 0) {
      mobileInfo?.classList.add('mobile-student-id-info--message-ready');
    }
  }

  /**
   * @param {boolean} preserveSelection
   * @param {'cb' | 'sid' | null} onlyRowScope - When both CMRP forms are shown, repopulate only this campus/term row (null = all rows).
   */
  populateCampus(preserveSelection = false, onlyRowScope = null) {
    const filteredCampus = this.config.ddcs_campus_entity.filter(
      campus => !campus.disabled_flag
    );
    const filteredCampusCount = filteredCampus.length;

    const { cb, sid } = cmrpSuffixPair();
    const dual = Boolean(cb && sid && cmrpShowBothForms());
    const campusTargets = [];
    if (dual) {
      const cCb = document.getElementById(cmrpId('campus', 'cb'));
      const cSid = document.getElementById(cmrpId('campus', 'sid'));
      if (cCb) campusTargets.push({ el: cCb, rowScope: 'cb' });
      if (cSid) campusTargets.push({ el: cSid, rowScope: 'sid' });
    } else {
      const c = this.getElementById('campus');
      if (c) campusTargets.push({ el: c, rowScope: null });
    }

    if (campusTargets.length === 0) {
      this.maybeMarkCourseDetailsSectionCampusUiReady(filteredCampusCount);
      this.updateStudentIdScheduleHint(filteredCampusCount === 1, filteredCampusCount);
      this.updateCampusTermUnavailableMessage();
      return;
    }

    const resetFormGroupStyles = el => {
      if (!el) return;
      el.style.display = '';
      el.style.visibility = '';
      el.style.height = '';
      el.style.overflow = '';
      el.style.margin = '';
      el.style.padding = '';
    };

    if (filteredCampusCount > 0) {
      for (const { el: campusSelect, rowScope } of campusTargets) {
        if (onlyRowScope != null && rowScope !== onlyRowScope) {
          continue;
        }
        const currentValue = preserveSelection ? campusSelect.value : '';
        campusSelect.innerHTML = '<option value="">Campus</option>';
        campusSelect.disabled = false;
        filteredCampus.forEach(campus => {
          const option = document.createElement('option');
          const label = decodeHtmlEntities(campus.name);
          option.value = label;
          cmrpStampOptionFullLabel(option, label);
          campusSelect.appendChild(option);
        });

        const rootId = rowScope
          ? cmrpCampusTermRootId(rowScope)
          : cmrpCampusTermRootId('activeCt');
        const ctRootEl = document.getElementById(rootId);
        // Prefer the campus select's form-group so student-id field order
        // (Student ID → Campus → Term) does not break single-campus hide.
        const desktopCampusFormGroup =
          campusSelect.closest('.form-group') ||
          ctRootEl?.querySelector('.cmrp-student-field--campus') ||
          ctRootEl?.querySelector('.form-group:first-child');

        if (filteredCampus.length === 1) {
          // One campus: auto-select and hide the campus control on every row (course-builder + student-id).
          campusSelect.value = decodeHtmlEntities(filteredCampus[0].name);
          const studentInlineRoot = ctRootEl?.classList?.contains(
            'cmrp-student-inline-row__inner',
          );
          const skipSingleCampusHide =
            Boolean(this.isEditingCampusTerm) && studentInlineRoot;
          if (desktopCampusFormGroup && !skipSingleCampusHide) {
            if (studentInlineRoot) {
              desktopCampusFormGroup.style.removeProperty('display');
            } else {
              desktopCampusFormGroup.style.display = 'none';
            }
          } else if (skipSingleCampusHide) {
            resetFormGroupStyles(desktopCampusFormGroup);
          }
          // Mark root so CSS can expand Term to full width (no leftover campus gap).
          if (ctRootEl && !skipSingleCampusHide) {
            ctRootEl.dataset.cmrpCampusUi = 'single';
          } else if (ctRootEl && skipSingleCampusHide) {
            delete ctRootEl.dataset.cmrpCampusUi;
          }
          const sidIsolated = cmrpIsolateStudentIdCampusTermFromCourseBuilder(
            rowScope === 'sid' ? 'sid' : 'cb',
          );
          if (!sidIsolated) {
            this.campus_entity_id = filteredCampus[0].campus_entity_id;
            this.show_division = cmrpResolveShowDivision(
              filteredCampus[0].is_division_used_flag,
            );
            this.setupDivisionField();
          }
          // Come-back / deep-link restore already reloads DDCS + products.
          // Auto-selecting the only campus here would also run populateTerms →
          // getFilteredData + every department/course option, which races restore
          // and can freeze the tab on large catalogs.
          if (!preserveSelection && !cmrpIsUrlCourseRestorePending()) {
            this.populateTerms(campusSelect);
          }
        } else {
          resetFormGroupStyles(desktopCampusFormGroup);
          if (ctRootEl) {
            delete ctRootEl.dataset.cmrpCampusUi;
          }
          if (preserveSelection && currentValue) {
            campusSelect.value = currentValue;
          }
        }

        cmrpSizeCampusTermRow(ctRootEl);
      }

      if (onlyRowScope == null) {
        const mobileCampusFormGroup = document.querySelector(
          '#mobile-campus-term-dropdowns .mobile-form-group:first-child',
        );
        if (filteredCampus.length === 1) {
          if (mobileCampusFormGroup) {
            mobileCampusFormGroup.style.display = 'none';
          }
          const mobileCampusSelect = document.querySelector(
            '#mobile-campus-term-dropdowns #campus',
          );
          if (mobileCampusSelect) {
            mobileCampusSelect.value = decodeHtmlEntities(filteredCampus[0].name);
          }
        } else {
          resetFormGroupStyles(mobileCampusFormGroup);
        }

        if (!preserveSelection && filteredCampus.length === 1) {
          if (
            window.courseMaterialsMobile &&
            typeof window.courseMaterialsMobile.updateCampusDisplay === 'function'
          ) {
            window.courseMaterialsMobile.updateCampusDisplay();
          }
        }
      }

      this.syncMobileDrawerCampusTermSelects();
      cmrpMirrorCampusTermBetweenForms();
    } else {
      this.disableSelects(['department', 'course', 'section']);
      if (!this.isCampusTermSelectionUnavailable()) {
        this.disableSelects(['campus', 'term']);
      }
    }

    this.showCampusTermDropdowns();
    this.maybeMarkCourseDetailsSectionCampusUiReady(filteredCampusCount);
    this.updateStudentIdScheduleHint(filteredCampusCount === 1, filteredCampusCount);
    if (!cmrpIsDivisionUsedGlobally()) {
      this.hideDivisionField();
    }
    this.updateCampusTermUnavailableMessage();
    window.cmrpCustomDropdowns?.syncAll?.();
  }
  async checkSession() {
    return cmrpGlobalCheckSession();
  }
  /**
   * @param {HTMLSelectElement} [fromCampusSelect] - Pass `this` from inline onchange so campus/term
   *   updates stay on the same row when both cb and sid forms exist (otherwise activeCt can mismatch).
   */
  async populateTerms(fromCampusSelect) {
    const campusSelect =
      fromCampusSelect && fromCampusSelect.tagName === 'SELECT'
        ? fromCampusSelect
        : this.getElementById('campus');
    if (!campusSelect) return;

    const scope = cmrpScopeFromCtSelect(campusSelect);
    const termSelect = document.getElementById(cmrpId('term', scope));
    if (!termSelect) return;

    const session_check = await this.checkSession();
    let studentId = false;
    if (session_check && session_check.status) {
      studentId = session_check.sessionID;
    }
    if (studentId) {
      if (!window.shopifySettings.studentIDActivation) {
        this.showCourseSelectionFields();
      }
    } else {
      if (!window.shopifySettings.studentIDActivation) {
        this.showCourseSelectionFields();
      }
    }

    const selectedCampus = campusSelect.value;

    await this.completeCampusChangeForRow(
      selectedCampus,
      campusSelect,
      termSelect,
    );
  }

  /**
   * Populate terms while preserving a specific term selection
   * @param {string} termToPreserve - The term value to preserve after population
   * @param {'cb'|'sid'} [rowScope] - When both forms exist, which campus/term row to update (default: course-builder cb).
   */
  populateTermsPreservingSelection(termToPreserve = '', rowScope) {
    const { cb, sid } = cmrpSuffixPair();
    let campusSelect;
    let termSelect;
    if (rowScope === 'sid' && sid) {
      campusSelect = document.getElementById(cmrpId('campus', 'sid'));
      termSelect = document.getElementById(cmrpId('term', 'sid'));
    } else if (cb) {
      campusSelect = document.getElementById(cmrpId('campus', 'cb'));
      termSelect = document.getElementById(cmrpId('term', 'cb'));
    } else {
      campusSelect = this.getElementById('campus');
      termSelect = this.getElementById('term');
    }
    if (!campusSelect || !termSelect) return;

    const selectedCampus = campusSelect.value;
    termSelect.innerHTML = '<option value="">Term</option>';

    if (selectedCampus) {
      const normalize = value =>
        String(value ?? '')
          .toLowerCase()
          .replace(/\s+/g, '');

      const filteredCampus = this.config.ddcs_campus_entity.filter(campus =>
        normalizeDdcsLabel(campus.name).includes(normalizeDdcsLabel(selectedCampus))
      );

      if (filteredCampus.length > 0) {
        const entityIdForTerms = filteredCampus[0].campus_entity_id;
        const sidIsolated = cmrpIsolateStudentIdCampusTermFromCourseBuilder(
          rowScope === 'sid' ? 'sid' : 'cb',
        );
        if (!sidIsolated) {
          this.campus_entity_id = entityIdForTerms;
          this.show_division = cmrpResolveShowDivision(
            filteredCampus[0].is_division_used_flag,
          );
          this.setupDivisionField();
        }

        const filteredTerms = window.courseData.ddcs_term.filter(
          term =>
            normalize(term.campus_entity_id) === normalize(entityIdForTerms),
        );

        if (filteredTerms.length > 0) {
          termSelect.disabled = false;
          filteredTerms.forEach(term => {
            const option = document.createElement('option');
            option.value = term.term_id;
            cmrpStampOptionFullLabel(option, decodeHtmlEntities(term.name));
            termSelect.appendChild(option);
          });

          // Restore the term selection if provided
          if (termToPreserve) {
            termSelect.value = termToPreserve;
            // Update the term_id for the preserved selection
            const filteredTerm = window.courseData.ddcs_term.filter(
              term => normalize(term.term_id) === normalize(termToPreserve),
            );
            if (filteredTerm.length > 0 && !sidIsolated) {
              this.term_id = filteredTerm[0].term_id;
            }
          }
        } else {
          if (rowScope === 'sid') {
            termSelect.innerHTML = '<option value="">Term</option>';
            termSelect.disabled = true;
          } else {
            this.disableSelects(['term', 'department', 'course', 'section']);
          }
        }
      } else {
        if (rowScope === 'sid') {
          termSelect.innerHTML = '<option value="">Term</option>';
          termSelect.disabled = true;
        } else {
          this.disableSelects(['term', 'department', 'course', 'section']);
        }
      }
    } else {
      if (rowScope === 'sid') {
        termSelect.innerHTML = '<option value="">Term</option>';
        termSelect.disabled = true;
      } else {
        this.disableSelects(['term', 'department', 'course', 'section']);
        this.hideDivisionField();
      }
    }

    this.syncMobileDrawerCampusTermSelects();
    cmrpMirrorCampusTermBetweenForms();
    this.syncMobileCampusTermDisplays();
    this.updateCampusTermUnavailableMessage();
  }
  async fetchMetaobjects(metaobjectType, query) {
    if (typeof window !== 'undefined' && window.fetchMetaobjectsOnce) {
      return window.fetchMetaobjectsOnce(metaobjectType, query);
    }

    const proxyPath = '/apps/shopifyData/metaobjects';
    const url = `${proxyPath}?type=${metaobjectType}&query=${query}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        const activeMetaobjects = result.data.filter(
          item => item.status === 'ACTIVE',
        );
        return activeMetaobjects;
      } else {
        throw new Error(result.error || 'Failed to fetch metaobjects');
      }
    } catch (error) {
      console.error('Error fetching metaobjects:', error);
      throw error;
    }
  }
  async fetchMetaobjectsData(termId, campusId) {
    // Usa helper global com cache quando disponível
    if (typeof window !== 'undefined' && window.fetchFilteredDataOnce) {
      return window.fetchFilteredDataOnce(termId, campusId);
    }

    // Fallback para implementação antiga (caso helper não esteja disponível)
    const proxyPath = '/apps/shopifyData/getFilteredData';
    const url = `${proxyPath}?campus_entity_id=${campusId}&term_id=${termId}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to fetch metaobjects');
      }
    } catch (error) {
      console.error('Error fetching metaobjects:', error);
      throw error;
    }
  }

  /**
   * Load campus/term DDCS metaobjects for a campus/term row.
   * By default writes into `window.courseData` (division/dept/course/section) for ProductDisplayManager.
   * When **both** student-id and course-builder forms exist and `rowScope === 'sid'`, that would
   * clobber the course-builder catalog — so we only write the shared `window.courseData` quartet
   * when `opts.allowSharedCourseDataOverwrite` is true (e.g. after student discovery, before materials refresh).
   * Otherwise sid data is stored on `window.courseData.__cmrpStudentIdRowDdcs` only.
   * @param {'sid' | 'cb'} rowScope
   * @param {{ allowSharedCourseDataOverwrite?: boolean }} [opts]
   */
  async hydrateDdcsDataForCampusTermRow(rowScope, opts = {}) {
    const campusSelect = document.getElementById(cmrpId('campus', rowScope));
    const termSelect = document.getElementById(cmrpId('term', rowScope));
    if (!campusSelect || !termSelect) return;

    const normalize = value =>
      String(value ?? '')
        .toLowerCase()
        .replace(/\s+/g, '');

    let campusEntityId = '';
    let termId = '';

    const campusName = campusSelect.value;
    if (campusName) {
      const filteredCampus = this.config.ddcs_campus_entity.filter(campus =>
        normalizeDdcsLabel(campus.name).includes(normalizeDdcsLabel(campusName)),
      );
      if (filteredCampus.length > 0) {
        campusEntityId = filteredCampus[0].campus_entity_id;
        const showDiv = cmrpResolveShowDivision(
          filteredCampus[0].is_division_used_flag,
        );
        if (!cmrpIsolateStudentIdCampusTermFromCourseBuilder(rowScope)) {
          this.campus_entity_id = campusEntityId;
          this.show_division = showDiv;
        }
      }
    }

    const selectedTerm = String(termSelect.value || '').trim();
    if (selectedTerm) {
      termId = selectedTerm;
      if (!cmrpIsolateStudentIdCampusTermFromCourseBuilder(rowScope)) {
        this.term_id = selectedTerm;
      }
    }

    if (!campusEntityId || !termId) {
      return;
    }

    const dataFilters = await this.fetchMetaobjectsData(termId, campusEntityId);

    const dualBothForms = cmrpShowBothForms();
    const sidRow = rowScope === 'sid';
    if (
      dualBothForms &&
      sidRow &&
      opts.allowSharedCourseDataOverwrite !== true
    ) {
      window.courseData.__cmrpStudentIdRowDdcs = {
        ddcs_campus_division: dataFilters.ddcs_campus_division,
        ddcs_campus_department: dataFilters.ddcs_campus_department,
        ddcs_course: dataFilters.ddcs_course,
        ddcs_section: dataFilters.ddcs_section,
      };
      return;
    }

    window.courseData.ddcs_campus_division = [];
    window.courseData.ddcs_campus_department = [];
    window.courseData.ddcs_course = [];
    window.courseData.ddcs_section = [];

    window.courseData.ddcs_campus_division = dataFilters.ddcs_campus_division;
    window.courseData.ddcs_campus_department = dataFilters.ddcs_campus_department;
    window.courseData.ddcs_course = dataFilters.ddcs_course;
    window.courseData.ddcs_section = dataFilters.ddcs_section;
  }

  /**
   * Complete the campus change after confirmation (or if no confirmation needed)
   * @param {string} selectedCampus - The selected campus value
   */
  async completeCampusChange(selectedCampus) {
    const cbSuf = cmrpSuffixPair().cb;
    const campusSelect = cbSuf
      ? document.getElementById(cmrpId('campus', 'cb'))
      : this.getElementById('campus');
    if (!campusSelect) return;
    const scope = cmrpScopeFromCtSelect(campusSelect);
    const termSelect = document.getElementById(cmrpId('term', scope));
    if (!termSelect) return;
    campusSelect.value = selectedCampus;
    await this.completeCampusChangeForRow(
      selectedCampus,
      campusSelect,
      termSelect,
    );
  }

  /**
   * Apply campus selection to a specific campus/term row (cb vs sid).
   */
  async completeCampusChangeForRow(selectedCampus, campusSelect, termSelect) {
    if (!campusSelect || !termSelect) return;

    campusSelect.value = selectedCampus;
    this.previousCampusValue = selectedCampus;

    const rowScope = cmrpScopeFromCtSelect(campusSelect);
    const skipFullClear =
      rowScope === 'sid' ||
      (this.isStudentIdMode() && !cmrpShowBothForms());
    if (!skipFullClear) {
      this.clearAllUserSelections(false, true);
    }

    const preserveTermInStudentIdMode =
      (rowScope === 'sid' || this.isStudentIdMode()) && termSelect.value;
    termSelect.innerHTML = '<option value="">Term</option>';

    if (selectedCampus) {
      const normalize = value =>
        String(value ?? '')
          .toLowerCase()
          .replace(/\s+/g, '');

      const filteredCampus = this.config.ddcs_campus_entity.filter(campus =>
        normalizeDdcsLabel(campus.name).includes(normalizeDdcsLabel(selectedCampus))
      );

      if (filteredCampus.length > 0) {
        const entityIdForTerms = filteredCampus[0].campus_entity_id;
        // Course-builder globals drive the cb DDCS row only. Student-id row must not
        // reset division visibility or entity ids used by course-builder when both forms show.
        if (!cmrpIsolateStudentIdCampusTermFromCourseBuilder(rowScope)) {
          this.campus_entity_id = entityIdForTerms;
          this.show_division = cmrpResolveShowDivision(
            filteredCampus[0].is_division_used_flag,
          );
          this.setupDivisionField();
        }

        const filteredTerms = window.courseData.ddcs_term.filter(
          term =>
            normalize(term.campus_entity_id) === normalize(entityIdForTerms),
        );

        const sortValuesByName = (a, b) => {
          const nameA = decodeHtmlEntities(a.name);
          const nameB = decodeHtmlEntities(b.name);

          const isNumA = !isNaN(nameA.charAt(0));
          const isNumB = !isNaN(nameB.charAt(0));
          
          if (isNumA && isNumB) {
            return parseInt(nameA) - parseInt(nameB); 
          }
          
          if (isNumA) {
            return -1; 
          }
          
          if (isNumB) {
            return 1; 
          }
          
          return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
        };
        const sortedValues = filteredTerms.sort(sortValuesByName);
        
        if (filteredTerms.length > 0) {
          if(filteredTerms.length == 1){
            termSelect.disabled = false;
            filteredTerms.forEach(term => {
              const option = document.createElement('option');
              option.value = term.term_id;
              cmrpStampOptionFullLabel(option, decodeHtmlEntities(term.name));
              option.selected = true;
              termSelect.appendChild(option);
            });
            await this.populateDepartmentsFromTerms(termSelect);
          }else{
            termSelect.disabled = false;
            filteredTerms.forEach(term => {
              const option = document.createElement('option');
              option.value = term.term_id;
              cmrpStampOptionFullLabel(option, decodeHtmlEntities(term.name));
              termSelect.appendChild(option);
            });
          }
          if (preserveTermInStudentIdMode && termSelect.querySelector('option[value="' + preserveTermInStudentIdMode + '"]')) {
            termSelect.value = preserveTermInStudentIdMode;
          }
        } else {
          this._disableTermRowForCompleteCampus(termSelect);
        }
      } else {
        this._disableTermRowForCompleteCampus(termSelect);
      }
    } else {
      this._disableTermRowForCompleteCampus(termSelect);
      if (rowScope !== 'sid') {
        this.hideDivisionField();
      }
    }

    this.syncMobileDrawerCampusTermSelects();
    cmrpMirrorCampusTermBetweenForms();
    this.syncMobileCampusTermDisplays();
    this.updateAddCourseButtonState();
    window.cmrpCustomDropdowns?.syncAll?.();

    if (rowScope !== 'sid') {
      cmrpSizeCampusTermRow(campusSelect.closest('.cmrp-cb-controls'));
    }
    this.updateCampusTermUnavailableMessage();
  }

  _disableTermRowForCompleteCampus(termSelect) {
    if (termSelect) {
      termSelect.innerHTML = '<option value="">Term</option>';
      termSelect.disabled = true;
    }
    if (cmrpShowBothForms() && cmrpScopeFromCtSelect(termSelect) === 'sid') {
      return;
    }
    if (cmrpShowBothForms()) {
      this.disableSelects(['department', 'course', 'section']);
    } else {
      this.disableSelects(['term', 'department', 'course', 'section']);
    }
  }

  /**
   * @param {HTMLSelectElement} [fromTermSelect] - Pass `this` from inline onchange for the correct row when cb+sid both exist.
   * @param {{ skipPageWideTermDeptReset?: boolean }} [opts] - When true on course-builder (`cb`), skip `clearAllTermSelections`
   *   (avoids `setURLSearchParams` + empty-state churn during `?campus=`/`?term=` hydration so `termSelect.value` stays committed).
   */
  async populateDepartmentsFromTerms(fromTermSelect, opts = {}) {
    // Division/department/course/section are always on the course-builder row (`-cb` when dual-form).
    // Do not default to `cmrpQueryDesktopCampusTerm('term')` — that follows `activeCt`, which can be
    // the student-id row while `?campus=` / `?term=` hydrated the cb row, leaving division disabled.
    const termSelect =
      fromTermSelect && fromTermSelect.tagName === 'SELECT'
        ? fromTermSelect
        : cmrpCourseBuilderCampusTermEl('term');
    if (!termSelect) {
      return;
    }

    const rowScope = cmrpScopeFromCtSelect(termSelect);
    const skipPageWideReset =
      opts?.skipPageWideTermDeptReset === true && rowScope === 'cb';

    const session_check = await this.checkSession();
    let studentId = false;
    if (session_check && session_check.status) {
      studentId = session_check.sessionID;
    }
    if (studentId) {
      if (!window.shopifySettings.studentIDActivation) {
        this.showCourseSelectionFields();
      }
    } else {
      if (!window.shopifySettings.studentIDActivation) {
        this.showCourseSelectionFields();
      }
    }
    window.CmrpCourseRowManager?.syncCourseRowsVisibility?.();

    // When both CMRP forms render, student-id campus/term must not touch the course-builder row
    // or overwrite `window.courseData` (shop-by-course uses that for dept/course/section options).
    if (cmrpShowBothForms() && rowScope === 'sid') {
      this.updateAddCourseButtonState();
      return;
    }

    if (skipPageWideReset) {
      this.clearDDCSSelections(false);
    } else if (
      rowScope === 'sid' ||
      (this.isStudentIdMode() && !cmrpShowBothForms()) ||
      cmrpIsUrlCourseRestorePending() ||
      (cmrpHasDeepLinkCourseParamsInUrl() &&
        (this.config.selectedFilters?.length ?? 0) === 0)
    ) {
      // Student-id flow, deep-link restore, or bootstrap before filters exist: reset DDCS
      // dropdowns only — do not wipe URL course params (shareable links).
      this.clearDDCSSelections();
    } else {
      this.clearAllTermSelections(true);
    }
    this.disableSelects(['division', 'department', 'course', 'section']);

    const campusSelect = document.getElementById(cmrpId('campus', rowScope));
    const campusName = campusSelect?.value;
    if (campusName) {
      const normalize = value =>
        String(value ?? '')
          .toLowerCase()
          .replace(/\s+/g, '');
      const campusPool =
        (Array.isArray(this.config.ddcs_campus_entity) &&
          this.config.ddcs_campus_entity.length > 0 &&
          this.config.ddcs_campus_entity) ||
        window.courseData?.ddcs_campus_entity ||
        [];
      const filteredCampus = campusPool.filter(campus =>
        normalizeDdcsLabel(campus.name).includes(normalizeDdcsLabel(campusName)),
      );
      if (filteredCampus.length > 0) {
        this.campus_entity_id = filteredCampus[0].campus_entity_id;
        this.show_division = cmrpResolveShowDivision(
          filteredCampus[0].is_division_used_flag,
        );
      }
    }

    const selectedTerm = String(
      termSelect.value ||
        termSelect.options[termSelect.selectedIndex]?.value ||
        '',
    ).trim();
    if (selectedTerm) {
      this.term_id = selectedTerm;
    }

    const dataFilters = await this.fetchMetaobjectsData(this.term_id, this.campus_entity_id);

    window.courseData.ddcs_campus_division = dataFilters.ddcs_campus_division;
    window.courseData.ddcs_campus_department = dataFilters.ddcs_campus_department;
    window.courseData.ddcs_course = dataFilters.ddcs_course;
    window.courseData.ddcs_section = dataFilters.ddcs_section;

    // URL restore owns product refresh + keeps Course #1 empty. Filling every
    // department/course <option> here duplicates that work and can freeze.
    if (cmrpIsUrlCourseRestorePending()) {
      this.updateAddCourseButtonState();
      return;
    }

    this.checkCampusTermSelection();

    if (selectedTerm) {
      this.setupDivisionField();
      if (this.show_division) {
        this.populateDivision();
      } else {
        this.populateDepartments();
      }
      
      // Load all courses when both campus and term are selected
      //this.loadAllCourses();
    } else {
    }
    this.updateAddCourseButtonState();
  }

  async populateDivision() {
    const divisionSelect = document.getElementById(cmrpId('division','cb'));
    if (!divisionSelect) {
      return;
    }

    divisionSelect.innerHTML = '<option value="">Division</option>';

    if (this.campus_entity_id) {
      const normalize = value =>
        String(value ?? '')
          .toLowerCase()
          .replace(/\s+/g, '');
      const filteredDivisions = (
        window.courseData?.ddcs_campus_division || []
      ).filter(
        d =>
          normalize(d.campus_entity_id) === normalize(this.campus_entity_id),
      );
      if (filteredDivisions.length > 0) {
        divisionSelect.disabled = false;
        filteredDivisions.forEach(division => {
          const option = document.createElement('option');
          option.value = division.campus_division_id;
          cmrpStampOptionFullLabel(option, decodeHtmlEntities(division.name));
          divisionSelect.appendChild(option);
        });
        // Auto-select only when this first field has a single option.
        if (filteredDivisions.length === 1) {
          divisionSelect.value = filteredDivisions[0].campus_division_id;
          this.populateDepartments();
        }
      } else {
        divisionSelect.disabled = true;
      }
    } else {
      divisionSelect.disabled = true;
    }
    this.updateAddCourseButtonState();
    window.cmrpCustomDropdowns?.syncAll?.();
  }

  async populateDepartments() {
    const departmentSelect = document.getElementById(cmrpId('department','cb'));
    const divisionSelect = document.getElementById(cmrpId('division','cb'));
    if (!departmentSelect) return;

    departmentSelect.innerHTML = '<option value="">Department</option>';

    // Clear child selects (course and section) when division changes
    const courseSelect = document.getElementById(cmrpId('course','cb'));
    const sectionSelect = document.getElementById(cmrpId('section','cb'));
    if (courseSelect) {
      courseSelect.innerHTML = '<option value="">Course</option>';
      courseSelect.disabled = true;
    }
    if (sectionSelect) {
      sectionSelect.innerHTML = '<option value="">Section</option>';
      sectionSelect.disabled = true;
    }

    const divisionField = document.getElementById(cmrpId('division_field','cb'));
    const hiddenDivision = divisionField?.hidden;

    if (hiddenDivision) {
      const filteredDepartments = window.courseData.ddcs_campus_department;
      
      this.populateDepartmentOptions(filteredDepartments, departmentSelect);
    } else {
      const selectedDivision = divisionSelect?.value;
      if (selectedDivision) {
        const normalize = value =>
          String(value).toLowerCase().replace(/\s+/g, '');
        const filteredDepartments = window.courseData.ddcs_campus_department.filter(
          dept =>
            normalize(dept.campus_division_id) === normalize(selectedDivision)
        );
        if (filteredDepartments.length > 0) {
          this.populateDepartmentOptions(filteredDepartments, departmentSelect);
        } 
      } else {
        this.disableSelects(['department', 'course', 'section']);
      }
    }
    this.updateAddCourseButtonState();
  }

  populateDepartmentOptions(departments, selectElement) {
    const departmentList = departments || [];
    if (departmentList.length > 0) {
      selectElement.disabled = false;
      departmentList.forEach(department => {
        const option = document.createElement('option');
        option.value = department.campus_department_id;
        cmrpStampOptionFullLabel(option, decodeHtmlEntities(department.name));
        selectElement.appendChild(option);
      });
      if (departmentList.length === 1) {
        selectElement.value = departmentList[0].campus_department_id;
        this.populateCourses();
      }
    } else {
      this.disableSelects(['department', 'course', 'section']);
    }
    window.cmrpCustomDropdowns?.syncAll?.();
  }

  async populateCourses() {
    const departmentSelect = document.getElementById(cmrpId('department','cb'));
    const courseSelect = document.getElementById(cmrpId('course','cb'));
    if (!departmentSelect || !courseSelect) return;

    const selectedDepartment = departmentSelect.value;
    courseSelect.innerHTML = '<option value="">Course</option>';

    // Clear child select (section) when department changes
    const sectionSelect = document.getElementById(cmrpId('section','cb'));
    if (sectionSelect) {
      sectionSelect.innerHTML = '<option value="">Section</option>';
      sectionSelect.disabled = true;
    }

    if (selectedDepartment) {
      const normalize = value =>
        String(value).toLowerCase().replace(/\s+/g, '');
      const filteredCourses = window.courseData.ddcs_course.filter(
        course =>
          normalize(course.campus_entity_id) ===
            normalize(this.campus_entity_id) &&
          normalize(course.campus_department_id) ===
            normalize(selectedDepartment)
      );

      if (filteredCourses.length > 0) {
        if (filteredCourses.length == 1) {
          courseSelect.disabled = false;
          filteredCourses.forEach(course => {
            const option = document.createElement('option');
            option.value = course.course_id;
            cmrpStampOptionFullLabel(option, decodeHtmlEntities(course.coursenumber));
            courseSelect.appendChild(option);
          });
          courseSelect.value = filteredCourses[0].course_id;
          this.populateSections();
        } else {
          courseSelect.disabled = false;
          filteredCourses.forEach(course => {
            const option = document.createElement('option');
            option.value = course.course_id;
            cmrpStampOptionFullLabel(option, decodeHtmlEntities(course.coursenumber));
            courseSelect.appendChild(option);
          });
        }
      } else {
        this.disableSelects(['course', 'section']);
      }
    } else {
      this.disableSelects(['course', 'section']);
    }
    this.updateAddCourseButtonState();
    window.cmrpCustomDropdowns?.syncAll?.();
  }

  async populateSections() {
    const courseSelect = document.getElementById(cmrpId('course','cb'));
    const sectionSelect = document.getElementById(cmrpId('section','cb'));
    if (!courseSelect || !sectionSelect) return;

    const selectedCourse = courseSelect.value;
    sectionSelect.innerHTML = '<option value="">Section</option>';

    if (selectedCourse) {
      const normalize = value =>
        String(value).toLowerCase().replace(/\s+/g, '');
      // Match `dev`: only sections for the selected course_id.
      const filteredSections = window.courseData.ddcs_section.filter(
        section =>
          normalize(section.campus_entity_id) === normalize(this.campus_entity_id) &&
          normalize(section.term_id) === normalize(this.term_id) &&
          normalize(section.course_id) === normalize(selectedCourse)
      );

      if (filteredSections.length > 0) {
        if (filteredSections.length == 1) {
          this.course_id = selectedCourse;
          sectionSelect.disabled = false;
          filteredSections.forEach(section => {
            const option = document.createElement('option');
            option.setAttribute('sectionGid', section.gid);
            const sectionLabel = decodeHtmlEntities(section.name);
            option.value = sectionLabel;
            cmrpStampOptionFullLabel(option, sectionLabel);
            sectionSelect.appendChild(option);
          });
          sectionSelect.value = decodeHtmlEntities(filteredSections[0].name);
        } else {
          this.course_id = selectedCourse;
          sectionSelect.disabled = false;
          filteredSections.forEach(section => {
            const option = document.createElement('option');
            option.setAttribute('sectionGid', section.gid);
            const sectionLabel = decodeHtmlEntities(section.name);
            option.value = sectionLabel;
            cmrpStampOptionFullLabel(option, sectionLabel);
            sectionSelect.appendChild(option);
          });
        }
      } else {
        this.disableSelects(['section']);
      }
    } else {
      this.disableSelects(['section']);
    }

    // Load all courses when both campus and term are selected
    //
    this.updateAddCourseButtonState();
    window.cmrpCustomDropdowns?.syncAll?.();
    window.setTimeout(() => {
      window.CmrpCourseRowManager?.onPrimarySectionsPopulated?.();
      window.cmrpCustomDropdowns?.syncAll?.();
    }, 0);
  }

  // Utility Methods
  disableSelects(selectIds) {
    selectIds.forEach(id => {
      const capitalizedId = id.charAt(0).toUpperCase() + id.slice(1);
      const placeholder = '<option value="">' + capitalizedId + '</option>';
      let nodes;
      if (id === 'campus') {
        const { cb, sid } = cmrpSuffixPair();
        const deskNodes = [];
        if (cb && sid && cmrpShowBothForms()) {
          const a = document.getElementById(cmrpId('campus', 'cb'));
          const b = document.getElementById(cmrpId('campus', 'sid'));
          if (a) deskNodes.push(a);
          if (b) deskNodes.push(b);
        } else {
          const desk = cmrpQueryDesktopCampusTerm('campus');
          if (desk) deskNodes.push(desk);
        }
        const mob = document.querySelector('#mobile-campus-term-dropdowns #campus');
        nodes = [...deskNodes, mob].filter(Boolean);
      } else if (id === 'term') {
        const { cb, sid } = cmrpSuffixPair();
        const deskNodes = [];
        if (cb && sid && cmrpShowBothForms()) {
          const a = document.getElementById(cmrpId('term', 'cb'));
          const b = document.getElementById(cmrpId('term', 'sid'));
          if (a) deskNodes.push(a);
          if (b) deskNodes.push(b);
        } else {
          const desk = cmrpQueryDesktopCampusTerm('term');
          if (desk) deskNodes.push(desk);
        }
        const mob = document.querySelector('#mobile-campus-term-dropdowns #term');
        nodes = [...deskNodes, mob].filter(Boolean);
      }
      if (nodes && nodes.length) {
        nodes.forEach(select => {
          select.innerHTML = placeholder;
          select.disabled = true;
        });
      } else {
        const scoped =
          id === 'division' ||
          id === 'department' ||
          id === 'course' ||
          id === 'section'
            ? cmrpId(id, 'cb')
            : id;
        const select = document.getElementById(scoped);
        if (select) {
          select.innerHTML = placeholder;
          select.disabled = true;
        }
      }
    });
    window.cmrpCustomDropdowns?.syncAll?.();
  }

  setupDivisionField() {
    const divisionField = document.getElementById(cmrpId('division_field', 'cb'));
    const divisionSelect = document.getElementById(cmrpId('division', 'cb'));
    if (!divisionField || !divisionSelect) {
      return;
    }

    const globalFlag = cmrpIsDivisionUsedGlobally();
    const willShow = this.show_division && globalFlag;

    if (willShow) {
      divisionField.hidden = false;
      divisionField.style.display = 'block';
      divisionSelect.innerHTML = '<option value="">Division</option>';
      divisionSelect.disabled = true;
    } else {
      this.hideDivisionField('setupDivisionField');
    }
  }

  hideDivisionField(caller = 'unknown') {
    const divisionField = document.getElementById(cmrpId('division_field', 'cb'));
    const divisionSelect = document.getElementById(cmrpId('division', 'cb'));
    if (!divisionField || !divisionSelect) return;

    divisionField.hidden = true;
    divisionField.style.display = 'none';
    divisionSelect.innerHTML = '<option value="">Division</option>';
    divisionSelect.disabled = true;
  }

  // Campus/Term Display Management
  showCampusTermDropdowns() {
    const { cb, sid } = cmrpSuffixPair();
    const rowScopes =
      cb && sid && cmrpShowBothForms() ? ['cb', 'sid'] : [null];
    const mobileDropdowns = document.getElementById('mobile-campus-term-dropdowns');
    if (mobileDropdowns) {
      mobileDropdowns.style.visibility = 'visible';
    }

    for (const rowScope of rowScopes) {
      const root = rowScope
        ? cmrpCampusTermRootId(rowScope)
        : cmrpCampusTermRootId('activeCt');
      const dropdowns = document.getElementById(root);
      const display = document.getElementById(
        cmrpId('campus-term-display', rowScope || 'activeCt'),
      );
      const campusSelect = rowScope
        ? document.getElementById(cmrpId('campus', rowScope))
        : cmrpQueryDesktopCampusTerm('campus');
      const termSelect = rowScope
        ? document.getElementById(cmrpId('term', rowScope))
        : cmrpQueryDesktopCampusTerm('term');

      if (dropdowns) {
        cmrpSetCampusTermDropdownContainerVisibility(dropdowns, 'show');
      }
      if (display) {
        display.style.display = 'none';
        display.classList.remove('show');
      }
      if (campusSelect) {
        campusSelect.disabled = false;
      }
      if (termSelect) {
        // Match populateTerms / completeCampusChangeForRow: term stays disabled until a campus is chosen.
        const hasCampus = Boolean(String(campusSelect?.value ?? '').trim());
        termSelect.disabled = !hasCampus;
      }
    }
    this.syncMobileDrawerCampusTermSelects();
  }

  showCampusTermDisplay(campusText, termText) {
    const root = cmrpCampusTermRootId('activeCt');
    const dropdowns = document.getElementById(root);
    const display = document.getElementById(cmrpId('campus-term-display', 'activeCt'));
    const textElement = document.getElementById(cmrpId('selected-campus-term-text', 'activeCt'));
    const campusTermDisplay = document.getElementById('selected-campus-display');
    // Hide dropdowns and show display when campus and term are selected
    if (dropdowns) {
      cmrpSetCampusTermDropdownContainerVisibility(dropdowns, 'hide');
    }
    if (display) {
      display.style.display = 'block';
      display.classList.add('show');
    }
    
    // Format display text based on available data
    let displayText = campusText;
    if (termText) {
      displayText = `${campusText} | ${termText}`;
    }
    
    if (textElement) textElement.textContent = displayText;
    
    // Also update the campus-term display for course section headers.
    // Multi mode still shows the first campus|term in the top bar.
    if (campusTermDisplay) {
      if (window.CourseMaterialsUtils?.renderCampusTermHeading) {
        CourseMaterialsUtils.renderCampusTermHeading(campusTermDisplay, {
          campus: campusText,
          term: termText,
          campusEntities: window.courseData?.ddcs_campus_entity,
        });
      } else {
        campusTermDisplay.textContent = displayText;
      }
    }
  }

  async editCampusTerm() {
    // Always proceed with editing directly
    await this.proceedWithCampusTermEdit();
  }

  /**
   * Proceed with campus/term editing after confirmation (or if no confirmation needed)
   */
  async proceedWithCampusTermEdit() {
    // Set editing flag to prevent auto-switching back to display mode
    this.isEditingCampusTerm = true;

    // Store current campus and term values before switching to edit mode
    const cbSuf = cmrpSuffixPair().cb;
    const campusSelect = cbSuf
      ? document.getElementById(cmrpId('campus', 'cb'))
      : cmrpQueryDesktopCampusTerm('campus');
    const termSelect = cbSuf
      ? document.getElementById(cmrpId('term', 'cb'))
      : cmrpQueryDesktopCampusTerm('term');
    const currentCampus = campusSelect?.value || '';
    const currentTerm = termSelect?.value || '';

    // Show the dropdowns (this also enables them)
    this.showCampusTermDropdowns();

    // Disable Student ID mode to show course selection dropdowns
    this.disableStudentIdMode();

    // Ensure dropdowns are populated while preserving current selections
    this.populateCampus(true);

    // If we had a campus selected, populate terms and restore term selection
    if (currentCampus && campusSelect) {
      campusSelect.value = currentCampus;
      this.populateTermsPreservingSelection(currentTerm);
    }

    // Check if we need to enable course selection after edit
    if (campusSelect && termSelect && campusSelect.value && termSelect.value) {
      await this.enableCourseSelectionAfterEdit(termSelect);
    }
  }

  /**
   * Enable course selection dropdowns after editing campus/term
   */
  async enableCourseSelectionAfterEdit(termSelectEl) {
    await this.populateDepartmentsFromTerms(
      termSelectEl ||
        document.getElementById(cmrpId('term', 'cb')) ||
        cmrpQueryDesktopCampusTerm('term'),
    );
  }

  /** Edit campus/term for the student-id row only (does not switch global course-builder mode). */
  async proceedWithStudentIdCampusTermEdit() {
    this.isEditingCampusTerm = true;
    const campusSelect = document.getElementById(cmrpId('campus', 'sid'));
    const termSelect = document.getElementById(cmrpId('term', 'sid'));
    if (!campusSelect || !termSelect) return;
    const currentCampus = campusSelect.value || '';
    const currentTerm = termSelect.value || '';

    this.showCampusTermDropdowns();
    this.populateCampus(true);

    if (currentCampus) {
      campusSelect.value = currentCampus;
      this.populateTermsPreservingSelection(currentTerm, 'sid');
    }
  }

  /**
   * Apply `?campus=` / `?term=` once after campus/term lists are ready (not on every populateCampus).
   */
  _scheduleQueryCampusTermHydration() {
    const run = async () => {
      if (this._queryCampusTermHydratedOnce) return;
      this._queryCampusTermHydratedOnce = true;
      try {
        await this.handleURLParameters();
      } catch (e) {
        console.error('[CMRP] handleURLParameters failed', e);
      }
    };
    if (window.__cmrpCampusTermFetchApplied === true) {
      setTimeout(run, 0);
    } else {
      document.addEventListener('cmrp:campusTermDataReady', run, { once: true });
    }
  }

  /**
   * Handle URL parameters for campus and term selection
   */
  async handleURLParameters() {
    const urlParams = new URLSearchParams(window.location.search);
    const campusParam = urlParams.get('campus');
    const termParam = urlParams.get('term');

    if (!campusParam) {
      try {
        const p = urlParams;
        const deep =
          p.get('shopBy') ||
          p.get('termId') ||
          p.get('departmentDisplayName') ||
          p.get('courseDisplayName') ||
          p.get('sectionDisplayName') ||
          p.get('divisionDisplayName');
      } catch (e) {
      }
      return;
    }

    const campusDecoded = decodeHtmlEntities(campusParam);
    const cbSuf = cmrpSuffixPair().cb;
    const campusSelect = cbSuf
      ? document.getElementById(cmrpId('campus', 'cb'))
      : this.getElementById('campus');
    if (!campusSelect) {
      return;
    }

    campusSelect.value = campusDecoded;
    await this.populateTerms(campusSelect);

    if (!termParam) {
      setTimeout(() => {
        this.showCampusTermDropdowns();
        const campusSelectRetry = cbSuf
          ? document.getElementById(cmrpId('campus', 'cb'))
          : this.getElementById('campus');
        if (campusSelectRetry) {
          campusSelectRetry.value = campusDecoded;
          this.completeCampusChange(campusDecoded);
        }
      }, 100);
      return;
    }

    const termSelect = cbSuf
      ? document.getElementById(cmrpId('term', 'cb'))
      : this.getElementById('term');
    if (!termSelect) {
      return;
    }

    const tp = String(termParam).trim();
    const matchTermIndex = sel => {
      const opts = Array.from(sel.options);
      return opts.findIndex(
        opt =>
          opt.value &&
          (opt.value === tp ||
            decodeHtmlEntities(cmrpOptionFullLabel(opt)) === decodeHtmlEntities(tp) ||
            decodeHtmlEntities(cmrpOptionFullLabel(opt)).toLowerCase() ===
              decodeHtmlEntities(tp).toLowerCase()),
      );
    };

    let idx = matchTermIndex(termSelect);
    if (idx <= 0) {
      await new Promise(r => setTimeout(r, 400));
      const retry = cbSuf
        ? document.getElementById(cmrpId('term', 'cb'))
        : this.getElementById('term');
      if (retry) idx = matchTermIndex(retry);
    }

    const finalTerm =
      cbSuf
        ? document.getElementById(cmrpId('term', 'cb'))
        : this.getElementById('term');
    if (!finalTerm || idx <= 0) {
      return;
    }

    const chosen = finalTerm.options[idx];
    finalTerm.selectedIndex = idx;
    if (chosen?.value) {
      finalTerm.value = chosen.value;
      this.term_id = chosen.value;
    }

    await this.populateDepartmentsFromTerms(finalTerm, {
      skipPageWideTermDeptReset: true,
    });
    this.showCampusTermDropdowns();
  }

  /**
   * Finish campus/term editing and return to normal mode
   */
  finishCampusTermEdit(opts = {}) {
    this.isEditingCampusTerm = false;
    // View Materials already has campus/term selected; rebuilding options can
    // fire term onchange → clearAllTermSelections and wipe the URL/results.
    if (opts.skipCampusRepopulate === true) return;
    // Re-sync campus/term chrome (single-campus student row hide, etc.) after leaving edit mode.
    this.populateCampus(true);
  }

  checkCampusTermSelection() {
    const cbSuf = cmrpSuffixPair().cb;
    const campusSelect = cbSuf
      ? document.getElementById(cmrpId('campus', 'cb'))
      : cmrpQueryDesktopCampusTerm('campus');
    const termSelect = cbSuf
      ? document.getElementById(cmrpId('term', 'cb'))
      : cmrpQueryDesktopCampusTerm('term');
    
    // Don't auto-switch to display mode if user is actively editing
    if (this.isEditingCampusTerm) {
      return false;
    }

    // Check if any courses have been added
    const hasCourses = this.config.selectedFilters && this.config.selectedFilters.length > 0;
    
    if (campusSelect?.value && termSelect?.value) {
      // Only show display mode if courses have been added
      if (hasCourses) {
        this.showCampusTermDropdowns();
        //this.showCampusTermDisplay(campusSelect.value, termSelect.options[termSelect.selectedIndex].text);
        return true;
      } else {
        // Keep dropdowns visible if no courses have been added
        this.showCampusTermDropdowns();
        return false;
      }
    } else {
      this.showCampusTermDropdowns();
      return false;
    }
  }

  // Validation
  validateRequiredFields() {
    const campus = cmrpCourseBuilderCampusTermEl('campus')?.value;
    const term = cmrpCourseBuilderCampusTermEl('term')?.value;
    const department = document.getElementById(cmrpId('department','cb'))?.value;
    const course = document.getElementById(cmrpId('course','cb'))?.value;
    const section = document.getElementById(cmrpId('section','cb'))?.value;

    return campus && term && department && course && section;
  }

  validateExistingFields() {
    var ret = true;
    const campusEl = cmrpCourseBuilderCampusTermEl('campus');
    const termEl = cmrpCourseBuilderCampusTermEl('term');
    const campus = campusEl?.value;
    const term = termEl?.value;
    const termText = termEl?.selectedOptions[0]?.text;
    const divisionEl = document.getElementById(cmrpId('division', 'cb'));
    const divisionLabel = divisionEl
      ? cmrpSelectSelectedFullLabel(divisionEl)
      : '';
    const division =
      divisionLabel && divisionLabel !== 'Division' && divisionLabel !== ''
        ? divisionLabel
        : '';
    const department = document.getElementById(cmrpId('department','cb'))?.value;
    const departmentText = document.getElementById(cmrpId('department','cb'))?.selectedOptions[0].text;
    const course = document.getElementById(cmrpId('course','cb'))?.value;
    const courseText = document.getElementById(cmrpId('course','cb'))?.selectedOptions[0].text;
    const section = document.getElementById(cmrpId('section','cb'))?.value;

    if (this.config.selectedFilters.length > 0) {
      for (const filter of this.config.selectedFilters) {
        if (
            (filter.selectedDivision || '') === division &&
            filter.selectedDepartment === departmentText &&
            filter.selectedsection === section &&
            filter.selectedcourse === courseText &&
            filter.selectedTerm === termText &&
            filter.selectedTermId === term &&
            filter.selectedCampus === campus
        ) {
          ret = false; 
          break; 
        }
      }
    } 

    return ret
  }
  validateRequiredFieldsStudent() {
    const sidSuf = cmrpSuffixPair().sid;
    const campus = (
      sidSuf
        ? document.getElementById(cmrpId('campus', 'sid'))
        : typeof window.cmrpQueryDesktopCampusTerm === 'function'
          ? window.cmrpQueryDesktopCampusTerm('campus')
          : null
    )?.value;
    const term = (
      sidSuf
        ? document.getElementById(cmrpId('term', 'sid'))
        : typeof window.cmrpQueryDesktopCampusTerm === 'function'
          ? window.cmrpQueryDesktopCampusTerm('term')
          : null
    )?.value;
    const studentId = document.getElementById(cmrpId('student-id','sid'))?.value;
    const hasStudentId = String(studentId ?? '').trim().length > 0;

    return Boolean(campus && term && hasStudentId);
  }

  /**
   * Copy for student-ID submit validation: omit "Campus" when there is only one campus option
   * (matches populateCampus single-campus UX where the campus row is hidden/auto-selected).
   */
  getStudentIdRequiredFieldsMessage() {
    const filteredCampus = (this.config.ddcs_campus_entity || []).filter(
      campus => !campus.disabled_flag
    );
    const parts = [];
    if (filteredCampus.length > 1) {
      parts.push('Campus');
    }
    parts.push('Term');
    parts.push(cmrpStudentIdLabel());
    return `Please select all required fields: ${parts.join(', ')}.`;
  }

  updateAddCourseButtonState() {
    // Multi-row: footer "+ Add Course" + View Materials are managed by CmrpCourseRowManager.
    if (window.CmrpCourseRowManager) {
      window.CmrpCourseRowManager.updateAddCourseButton?.();
      window.CmrpCourseRowManager.updateViewMaterialsButton?.();
      return;
    }

    const addButton = document.getElementById(cmrpId('add-course','cb'));
    if (!addButton) return;

    // Keep Add Course always clickable so validation feedback appears on click.
    // `handleAddCourse()` is the source of truth for required-field warnings.
    addButton.disabled = false;
    addButton.classList.remove('disabled');
  }

  toggleAddCourseLoading(isLoading) {
    const button = document.getElementById(cmrpId('add-course','cb'));
    if (!button) return;

    const textEl = button.querySelector('.buy-by-course-builder__add-btn__text');

    if (isLoading) {
      if (!button.dataset.originalText) {
        // Never set `button.textContent` — it destroys the plus icon markup in `.buy-by-course-builder__add-btn__icon`.
        button.dataset.originalText = textEl
          ? (textEl.textContent || '').trim()
          : (button.textContent || '').trim();
      }
      button.disabled = true;
      if (textEl) {
        textEl.textContent = 'ADDING...';
      } else {
        button.textContent = 'ADDING...';
      }
    } else {
      if (button.dataset.originalText) {
        if (textEl) {
          textEl.textContent = button.dataset.originalText;
        } else {
          button.textContent = button.dataset.originalText;
        }
        delete button.dataset.originalText;
      } else if (textEl) {
        textEl.textContent = 'Add Course';
      } else {
        button.textContent = 'ADD COURSE';
      }
      this.updateAddCourseButtonState();
    }
  }
  async fetchProducts(queryData) {
    const proxyPath = '/apps/shopifyData/products';
    const url = `${proxyPath}?query=${queryData}`;

    try {
        const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'same-origin'
        });

        if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          return result.products;
        } else {
        throw new Error(result.error || 'Failed to fetch products');
        }

    } catch (error) {
        console.error('Error fetching products:', error);
        throw error;
    }
  }
  // Course Actions
  async handleAddCourse() {
    const courseBuilderWarningId = cmrpId('course-builder-error-warning', 'cb');
    if (typeof hideWarningMessage === 'function') {
      hideWarningMessage(courseBuilderWarningId);
    }

    if (!this.validateRequiredFields()) {
      if (
        window.courseMaterialsPageInstance &&
        typeof window.courseMaterialsPageInstance.showErrorMessage ===
          'function'
      ) {
        window.courseMaterialsPageInstance.showErrorMessage(
          'Please select all required fields: Campus, Term, Division, Department, Course, and Section.',
          '',
          false,
          { warningIds: [courseBuilderWarningId] }
        );
      } else {
        alert('Please select all required fields: Campus, Term, Division, Department, Course, and Section.');
      }
      return;
    }
    if (!this.validateExistingFields()) {
      if (
        window.courseMaterialsPageInstance &&
        typeof window.courseMaterialsPageInstance.showErrorMessage ===
          'function'
      ) {
        window.courseMaterialsPageInstance.showErrorMessage(
          'Your course selection is already being displayed.  Please enter new values.',
          '',
          false,
          { warningIds: [courseBuilderWarningId] }
        );
      } else {
        alert('Your course selection is already being displayed.  Please enter new values.');
      }
      return;

    }
    hideWarningMessage(cmrpId('student-id-error-warning', 'sid'));
    hideWarningMessage('mobile-student-id-error-warning');
    const selectElement = document.getElementById(cmrpId('section','cb'));

    this.toggleAddCourseLoading(true);

    try {
      hideWarningMessage(cmrpId('student-id-error-warning', 'sid'));
      const selectElement = document.getElementById(cmrpId('section','cb'));

      const chips = document.querySelectorAll('.course-chip');
      const chip_data =  Array.from(chips).map(chip => ({
        division: chip.getAttribute('data-division'),
        department: chip.getAttribute('data-department'),
        course: chip.getAttribute('data-course'),
        section: chip.getAttribute('data-section'),
        key: chip.getAttribute('data-course-key')
      }));
      const courseData = this.getCourseFormData();
      const objCourse = this.createCourseObject(courseData);
      
      // Add course to selected courses display
      const divisionEl = document.getElementById(cmrpId('division', 'cb'));
      const divisionLabel = cmrpSelectSelectedFullLabel(divisionEl);
      let division =
        divisionLabel && divisionLabel !== 'Division' ? divisionLabel : '';

      const departmentEl = document.getElementById(cmrpId('department', 'cb'));
      const departmentLabel = cmrpSelectSelectedFullLabel(departmentEl);
      let department =
        departmentLabel && departmentLabel !== 'Department'
          ? departmentLabel
          : '';

      const courseEl = document.getElementById(cmrpId('course', 'cb'));
      const courseLabel = cmrpSelectSelectedFullLabel(courseEl);
      let course =
        courseLabel && courseLabel !== 'Course' ? courseLabel : '';

      const sectionSelect = document.getElementById(cmrpId('section', 'cb'));
      const sectionValue = sectionSelect?.value?.trim() || '';
      const sectionTextRaw = sectionSelect
        ? cmrpSelectSelectedFullLabel(sectionSelect)
        : '';
      const sectionText =
        sectionTextRaw && sectionTextRaw !== 'Section' && sectionTextRaw.trim() !== ''
          ? sectionTextRaw.trim()
          : '';
      if (department && course && sectionValue) {
        // Use option value for identity (matches selectedFilters + syncBubblesFromFilters); label can stay human-readable.
        this.addSelectedCourse(
          department,
          course,
          sectionValue,
          sectionText || sectionValue,
          division,
        );
      }
      document.dispatchEvent(
        new CustomEvent('sidebar:courseAdded', {
          detail: { course: objCourse },
        })
      );

      // Finish campus/term editing since user has made a selection
      this.finishCampusTermEdit();

      await this.resetCourseBuilderLineKeepCampusTerm();

      this.resetCmrpStudentRowAfterCourseBuilderSubmit();

      if (window.innerWidth <= 968 && !cmrpIsCourseMaterialsResultsPage()) {
        window.courseMaterialsSidebar?.closeMobileDrawer?.();
      }
    } finally {
      this.toggleAddCourseLoading(false);
    }
}

async handleAddStudent() {
  if (!this.validateRequiredFieldsStudent()) {
    // Use the page's error display method
    if (
      window.courseMaterialsPageInstance &&
      typeof window.courseMaterialsPageInstance.showErrorMessage ===
        'function'
    ) {
      window.courseMaterialsPageInstance.showErrorMessage(
        `Please select all required fields: Campus, Term, ${cmrpStudentIdLabel()}.`
      );
    } else {
      alert(`Please select all required fields: Campus, Term, ${cmrpStudentIdLabel()}.`);
    }
    return;
  }

  this.toggleSubmitLoading(true);
  // Full-page CMRP loader (#loading-state); closed in handleStudentSubmission on API settle
  if (typeof window.showLoadingState === 'function') {
    window.showLoadingState();
  }

  const studentData = this.getStudentFormData();

  document.dispatchEvent(
    new CustomEvent('sidebar:studentSubmitted', {
      detail: {
        studentData,
        onComplete: async () => {
          this.toggleSubmitLoading(false);
          // Finish campus/term editing since user has made a selection
          this.finishCampusTermEdit();
        },
        onError: () => {
          this.toggleSubmitLoading(false);
          if (typeof window.hideLoadingState === 'function') {
            window.hideLoadingState();
          }
        },
      },
    })
  );
}

  getCourseFormData() {
    const cbSuf = cmrpSuffixPair().cb;
    const campusForCourse = cbSuf
      ? document.getElementById(cmrpId('campus', 'cb'))
      : cmrpQueryDesktopCampusTerm('campus');
    const termForCourse = cbSuf
      ? document.getElementById(cmrpId('term', 'cb'))
      : cmrpQueryDesktopCampusTerm('term');
    const selectElement = document.getElementById(cmrpId('section', 'cb'));
    if (!selectElement) {
      return {
        selectedDepartment: '',
        selectedCampus: campusForCourse?.value || '',
        selectedTerm: '',
        selectedcourse: '',
        selectedsection: '',
        selectedsectionGid: '',
        selectedDivision: '',
      };
    }

    const optionToFind = Array.from(selectElement.options).find(
      option => option.value === selectElement.value,
    );
    const sectionGid = optionToFind?.getAttribute('sectiongid');

    let department = cmrpSelectSelectedFullLabel(
      document.getElementById(cmrpId('department', 'cb')),
    );

    let division = cmrpSelectSelectedFullLabel(
      document.getElementById(cmrpId('division', 'cb')),
    );
    let term = cmrpSelectSelectedFullLabel(termForCourse);

    let course = cmrpSelectSelectedFullLabel(
      document.getElementById(cmrpId('course', 'cb')),
    );

    return {
      selectedDepartment: department != 'Department' && department != '' ? department : '',
      selectedCampus: campusForCourse?.value || '',
      selectedTerm: term != 'Term' && term != '' ? term : '',
      selectedcourse: course != 'Course' && course != '' ? course : '',
      selectedsection: document.getElementById(cmrpId('section','cb'))?.value || '',
      selectedsectionGid: sectionGid || '',
      selectedDivision: division != 'Division' && division != '' ? division : '',
    };
  }

  getStudentFormData() {
    const sidSuf = cmrpSuffixPair().sid;
    const campusSid = sidSuf
      ? document.getElementById(cmrpId('campus', 'sid'))
      : cmrpQueryDesktopCampusTerm('campus');
    const termSid = sidSuf
      ? document.getElementById(cmrpId('term', 'sid'))
      : cmrpQueryDesktopCampusTerm('term');
    return {
      selectedCampus: campusSid?.value || '',
      selectedTerm: termSid?.value || '',
      studentId: document.getElementById(cmrpId('student-id','sid'))?.value || '',
    };
  }

  createCourseObject(courseData) {
    let selectedTermId = '';
    const selectedSection =
      courseData.selectedsectionGid && window.courseData?.ddcs_section?.length
        ? window.courseData.ddcs_section.find(
            section => section.gid === courseData.selectedsectionGid
          )
        : null;
    if (selectedSection?.term_id) {
      selectedTermId = selectedSection.term_id;
    }

    if (!selectedTermId && courseData.selectedTerm) {
      const filteredTerm = window.courseData.ddcs_term.filter(term =>
        normalizeDdcsLabel(term.name).includes(normalizeDdcsLabel(courseData.selectedTerm))
      );
      if (filteredTerm.length > 0) {
        selectedTermId = filteredTerm[0].term_id;
      }
    }

    return {
      selectedDepartment: courseData.selectedDepartment,
      selectedCampus: courseData.selectedCampus,
      selectedTerm: courseData.selectedTerm,
      selectedTermId: selectedTermId,
      selectedcourse: courseData.selectedcourse,
      selectedsection: courseData.selectedsection,
      selectedDivision: courseData.selectedDivision,
      selectedsectionGid: courseData.selectedsectionGid,
      selectedsectionId: selectedSection?.section_id || '',
      adoption_status:
        selectedSection?.adoption_status != null &&
        selectedSection.adoption_status !== ''
          ? String(selectedSection.adoption_status)
          : '',
    };
  }

  toggleSubmitLoading(isLoading) {
    const button = document.getElementById(cmrpId('submit-student-id','sid'));
    if (!button) return;

    const btnText = button.querySelector('.btn-text');
    const btnSpinner = button.querySelector('.btn-spinner');

    if (isLoading) {
      button.disabled = true;
      if (btnText) btnText.textContent = 'SUBMITTING...';
      if (btnSpinner) btnSpinner.style.display = 'inline-block';
    } else {
      button.disabled = false;
      if (btnText) btnText.textContent = 'SUBMIT';
      if (btnSpinner) btnSpinner.style.display = 'none';
    }
  }

  // Warning Management
  showCampusChangeWarning() {
    if (typeof showWarningMessage === 'function') {
      showWarningMessage(cmrpId('campus-change-warning', 'activeCt'));
    }
  }

  hideCampusChangeWarning() {
    if (typeof hideWarningMessage === 'function') {
      hideWarningMessage(cmrpId('campus-change-warning', 'activeCt'));
    }
  }

  /**
   * Handle campus change confirmation - user chose to continue
   * @param {string} warningId - The warning message ID
   */
  handleCampusChangeAccept(warningId) {
    // Hide the warning message
    this.hideCampusChangeWarning();

    // Proceed with the campus change (this will clear selections)
    if (this.pendingCampusChange && this.pendingCampusChange.selectedCampus) {
      this.completeCampusChange(this.pendingCampusChange.selectedCampus);
      this.pendingCampusChange = null;
    }
  }

  /**
   * Handle campus change cancellation - user chose to cancel
   * @param {string} warningId - The warning message ID
   */
  handleCampusChangeCancel(warningId) {
    // Hide the warning message
    this.hideCampusChangeWarning();

    // Cancel the pending campus change, keep selections intact
    this.pendingCampusChange = null;

    // Campus dropdown is already reset to previous value in populateTerms()
    // No further action needed - selections remain intact
  }

  // State Management
  clearAllUserSelections(clearCampus = false, clearTerm = false) {
    document.dispatchEvent(
      new CustomEvent('sidebar:clearSelections', {
        detail: { clearCampus, clearTerm },
      })
    );

    if (clearTerm) {
      const termSelect = cmrpCourseBuilderCampusTermEl('term');
      if (termSelect) {
        termSelect.innerHTML = '<option value="">Term</option>';
        termSelect.disabled = true;
      }
    }

    this.clearDDCSSelections();
  }
  clearAllTermSelections(clearTerm = false) {
    document.dispatchEvent(
      new CustomEvent('sidebar:clearAllTermSelections', {
        detail: { clearTerm },
      })
    );

    this.clearDDCSSelections();
  }

  clearDDCSSelections(enableFirstAvailable = false) {
    this.campus_department_id = '';
    this.course_id = '';
    this.division_id = '';

    const courseSelect = document.getElementById(cmrpId('course','cb'));
    const sectionSelect = document.getElementById(cmrpId('section','cb'));

    if (courseSelect) {
      courseSelect.innerHTML = '<option value="">Course</option>';
      courseSelect.disabled = true;
    }
    if (sectionSelect) {
      sectionSelect.innerHTML = '<option value="">Section</option>';
      sectionSelect.disabled = true;
    }

    const shouldShowDivision = this.show_division;
    const divisionSelect = document.getElementById(cmrpId('division','cb'));
    const departmentSelect = document.getElementById(cmrpId('department','cb'));

    if (enableFirstAvailable && shouldShowDivision) {
      if (divisionSelect) {
        divisionSelect.innerHTML = '<option value="">Division</option>';
        this.populateDivision();
      }
      if (departmentSelect) {
        departmentSelect.innerHTML = '<option value="">Department</option>';
        departmentSelect.disabled = true;
      }
    } else if (enableFirstAvailable && !shouldShowDivision) {
      if (divisionSelect) {
        divisionSelect.innerHTML = '<option value="">Division</option>';
        divisionSelect.disabled = true;
      }
      if (departmentSelect) {
        departmentSelect.innerHTML = '<option value="">Department</option>';
        this.populateDepartments();
      }
    } else {
      if (divisionSelect) {
        divisionSelect.innerHTML = '<option value="">Division</option>';
        divisionSelect.disabled = true;
      }
      if (departmentSelect) {
        departmentSelect.innerHTML = '<option value="">Department</option>';
        departmentSelect.disabled = true;
      }
    }

    this.updateAddCourseButtonState();
  }

  /**
   * Clear division→section for the course-builder row while keeping campus + term as-is,
   * then re-enable the first DDCS step (division or department) for the current term.
   */
  async resetCourseBuilderLineKeepCampusTerm() {
    const campusSelect = cmrpCourseBuilderCampusTermEl('campus');
    const termSelect = cmrpCourseBuilderCampusTermEl('term');
    const campusName = campusSelect?.value;
    const selectedTerm = termSelect?.value;

    const normalize = value =>
      String(value ?? '')
        .toLowerCase()
        .replace(/\s+/g, '');
    const campusPool =
      (Array.isArray(this.config.ddcs_campus_entity) &&
        this.config.ddcs_campus_entity.length > 0 &&
        this.config.ddcs_campus_entity) ||
      window.courseData?.ddcs_campus_entity ||
      [];

    if (campusName && campusPool.length) {
      const filteredCampus = campusPool.filter(campus =>
        normalizeDdcsLabel(campus.name).includes(normalizeDdcsLabel(campusName)),
      );
      if (filteredCampus.length > 0) {
        this.campus_entity_id = filteredCampus[0].campus_entity_id;
        this.show_division = cmrpResolveShowDivision(
          filteredCampus[0].is_division_used_flag,
        );
      }
    }
    if (selectedTerm) {
      this.term_id = selectedTerm;
    }

    this.setupDivisionField();
    this.clearDDCSSelections(false);

    if (selectedTerm && this.campus_entity_id) {
      // Do not empty DDCS first — a parallel refresh/display pass still reads it.
      // Only fetch when this campus/term has no section rows loaded yet.
      const hasDdcs = (window.courseData?.ddcs_section || []).length > 0;
      if (!hasDdcs) {
        try {
          const dataFilters = await this.fetchMetaobjectsData(
            this.term_id,
            this.campus_entity_id,
          );
          if (dataFilters) {
            window.courseData.ddcs_campus_division =
              dataFilters.ddcs_campus_division;
            window.courseData.ddcs_campus_department =
              dataFilters.ddcs_campus_department;
            window.courseData.ddcs_course = dataFilters.ddcs_course;
            window.courseData.ddcs_section = dataFilters.ddcs_section;
          }
        } catch (e) {
          console.error(
            '[CMRP] resetCourseBuilderLineKeepCampusTerm refetch failed',
            e,
          );
        }
      }
      this.setupDivisionField();
      if (this.show_division) {
        await this.populateDivision();
      } else {
        await this.populateDepartments();
      }
    }

    this.checkCampusTermSelection();
    this.updateAddCourseButtonState();
    window.CmrpCourseRowManager?.resetExtraRows?.();
  }

  // Clear all course fields after adding a course to allow easy addition of next course
  clearSectionForNextAddition() {
    const divisionSelect = document.getElementById(cmrpId('division','cb'));
    const departmentSelect = document.getElementById(cmrpId('department','cb'));
    const courseSelect = document.getElementById(cmrpId('course','cb'));
    const sectionSelect = document.getElementById(cmrpId('section','cb'));

    if (divisionSelect) {
      divisionSelect.value = '';
    }
    if (departmentSelect) {
      departmentSelect.value = '';
    }
    if (courseSelect) {
      courseSelect.value = '';
    }
    if (sectionSelect) {
      sectionSelect.value = '';
    }

    // Re-enable the dropdowns cascade for next course selection
    if (this.show_division) {
      this.populateDivision();
    } else {
      this.populateDepartments();
    }

    this.updateAddCourseButtonState();
  }

  /**
   * When both CMRP forms are visible: clear the student-ID form so it looks untouched
   * after a successful shop-by-course submission.
   */
  resetCmrpStudentRowAfterCourseBuilderSubmit() {
    if (!cmrpShowBothForms()) return;

    this.isEditingCampusTerm = false;

    const studentInput = document.getElementById(cmrpId('student-id', 'sid'));
    if (studentInput) studentInput.value = '';
    try {
      sessionStorage.removeItem(CMRP_STUDENT_ID_PREFILL_KEY);
    } catch (e) {
      /* ignore */
    }
    if (typeof hideWarningMessage === 'function') {
      hideWarningMessage(cmrpId('student-id-error-warning', 'sid'));
      hideWarningMessage('mobile-student-id-error-warning');
    }

    const termSid = document.getElementById(cmrpId('term', 'sid'));
    if (termSid) {
      termSid.innerHTML = '<option value="">Term</option>';
      termSid.disabled = true;
    }
    const campusSid = document.getElementById(cmrpId('campus', 'sid'));
    if (campusSid) {
      campusSid.value = '';
    }

    this.populateCampus(false, 'sid');
    this.syncMobileDrawerCampusTermSelects();
    window.applyCmrpCourseDetailsSectionFlowUi?.();
    anchorCmrpProductsPanel();
  }

  /**
   * When both CMRP forms are visible: clear the course-builder row so it looks untouched
   * after a student-ID submission (page clears filters/grid separately).
   */
  resetCmrpCourseBuilderRowAfterStudentSubmit() {
    if (!cmrpShowBothForms()) return;

    this.isEditingCampusTerm = false;
    this.allCoursesData = [];
    this.hideAllCoursesList();

    this.clearDDCSSelections(false);

    const termCb = document.getElementById(cmrpId('term', 'cb'));
    if (termCb) {
      termCb.innerHTML = '<option value="">Term</option>';
      termCb.disabled = true;
    }
    const campusCb = document.getElementById(cmrpId('campus', 'cb'));
    if (campusCb) {
      campusCb.value = '';
    }

    const chipBox = document.getElementById(cmrpId('selected-courses-container', 'cb'));
    if (chipBox) chipBox.innerHTML = '';

    this.populateCampus(false, 'cb');
    this.syncMobileDrawerCampusTermSelects();
    this.updateAddCourseButtonState();
    window.applyCmrpCourseDetailsSectionFlowUi?.();
    anchorCmrpProductsPanel();
  }

  /**
   * Enable Student ID mode - hide course selection dropdowns
   */
  enableStudentIdMode() {
    cmrpPersistFlow('student-id');
    const filtersPanel = document.getElementById('filters-section');
    if (filtersPanel) {
      filtersPanel.classList.add('student-id-mode');
      filtersPanel.classList.remove('cmrp-filters-panel--landing-chrome');
    }

    this.hideCourseSelectionFields();
    window.applyCmrpCourseDetailsSectionFlowUi?.();
    anchorCmrpProductsPanel();
  }

  /**
   * Disable Student ID mode - show course selection dropdowns
   * @param {boolean} [preserveStudentIdInput=false] - When true, keep desktop/mobile student ID fields (e.g. mobile drawer tab switch before submit)
   */
  disableStudentIdMode(preserveStudentIdInput = false) {
    cmrpPersistFlow('course-builder');
    const filtersPanel = document.getElementById('filters-section');
    if (filtersPanel) {
      filtersPanel.classList.remove('student-id-mode');
      if (cmrpStudentIdThemeEnabled()) {
        filtersPanel.classList.add('cmrp-filters-panel--landing-chrome');
      }
    }

    if (!preserveStudentIdInput) {
      const studentIdInput = document.getElementById(cmrpId('student-id','sid'));
      if (studentIdInput) {
        studentIdInput.value = '';
      }
      const mobileStudentIdInput = document.getElementById('mobile-student-id-input');
      if (mobileStudentIdInput) {
        mobileStudentIdInput.value = '';
      }
    }

    // Show course selection fields when disabling Student ID mode
    this.showCourseSelectionFields();
    window.applyCmrpCourseDetailsSectionFlowUi?.();
    anchorCmrpProductsPanel();
  }

  /**
   * Switch to manual course selection mode.
   * Persists course-builder flow in sessionStorage and reloads without stale results in the URL.
   */
  switchToManualCourseSelection() {
    cmrpPersistFlow('course-builder');
    if (typeof window.setCmrpInitialFlowPreference === 'function') {
      window.setCmrpInitialFlowPreference('course-builder');
    }
    try {
      sessionStorage.removeItem('sessionToken');
    } catch (e) {
      /* ignore */
    }
    cmrpNavigateForFlowSwitch('course-builder');
  }

  /**
   * Switch to shop-by-student-id flow.
   * Persists student-id flow in sessionStorage and reloads without stale results in the URL.
   */
  switchToStudentIdFlow() {
    cmrpPersistFlow('student-id');
    if (typeof window.setCmrpInitialFlowPreference === 'function') {
      window.setCmrpInitialFlowPreference('student-id');
    }
    cmrpNavigateForFlowSwitch('student-id');
  }

  hideCourseSelectionFields() {
    if (cmrpShowBothForms()) {
      return;
    }
    cmrpCourseBuilderCourseLineFieldEls().forEach(el => {
      el.style.display = 'none';
    });
    window.CmrpCourseRowManager?.syncCourseRowsVisibility?.();

    // Also hide the "What's the course name?" title if it exists separately
    const courseTitles = document.querySelectorAll('h3, .section-title');
    courseTitles.forEach(title => {
      if (
        title.textContent &&
        title.textContent.includes("What's the course name")
      ) {
        title.style.display = 'none';
      }
    });
  }

  showCourseSelectionFields() {
    if (cmrpShowBothForms()) {
      return;
    }
    cmrpCourseBuilderCourseLineFieldEls().forEach(el => {
      el.style.display = '';
    });
    window.CmrpCourseRowManager?.syncCourseRowsVisibility?.();

    // Show the "What's the course name?" title
    const courseTitles = document.querySelectorAll('h3, .section-title');
    courseTitles.forEach(title => {
      if (
        title.textContent &&
        title.textContent.includes("What's the course name")
      ) {
        title.style.display = '';
      }
    });
  }

  // All Courses List Management
  initializeAllCoursesList() {
    this.selectedCoursesFromList = new Set();
    this.allCoursesData = [];
  }

  /**
   * Initialize mobile trigger button visibility
   */
  initializeMobileTrigger() {
    const mobileTrigger = document.getElementById('mobile-course-trigger');
    const drawer = document.getElementById('mobile-drawer-section');
    const backdrop = document.getElementById('mobile-drawer-backdrop');

    if (!drawer || !backdrop) return;

    if (mobileTrigger && drawer && backdrop) {
      // Check if we're on mobile
      const isMobile = window.innerWidth <= 480;
      
      if (isMobile) {
        mobileTrigger.style.display = 'block';
        drawer.style.display = 'none';
        // Don't force backdrop to display: none, let CSS handle it
      } else {
        mobileTrigger.style.display = 'none';
        drawer.style.display = 'none';
        backdrop.style.display = 'none';
      }
    }
  }

  loadAllCourses() {
    const campusSelect = cmrpCourseBuilderCampusTermEl('campus');
    const termSelect = cmrpCourseBuilderCampusTermEl('term');
    const divisionSelect = document.getElementById(cmrpId('division','cb'));
    const departmentSelect = document.getElementById(cmrpId('department','cb'));
    const courseSelect = document.getElementById(cmrpId('course','cb'));
    
    if (!campusSelect?.value || !termSelect?.value || !departmentSelect?.value || !courseSelect?.value) {
      this.hideAllCoursesList();
      return;
    }

    const isNarrowViewport = window.innerWidth <= 968;
    const useMobileAllCoursesList =
      isNarrowViewport && !cmrpIsCourseMaterialsResultsPage();

    this.showCoursesLoading();

    try {
      if (useMobileAllCoursesList) {
        this.loadAllCoursesForMobile();
      } else {
        // Desktop: use existing data and existing logic
        //this.allCoursesData = this.getAllAvailableCourses();
        //this.renderAllCoursesList();
        //this.hideCoursesLoading();
      }

    } catch (error) {
      console.error('Error loading all courses:', error);
      this.hideAllCoursesList();
      this.hideCoursesLoading();
    }
  }

  async loadAllCoursesForMobile() {
    const campusSelect = cmrpCourseBuilderCampusTermEl('campus');
    const termSelect = cmrpCourseBuilderCampusTermEl('term');
    const divisionSelect = document.getElementById(cmrpId('division','cb'));
    const departmentSelect = document.getElementById(cmrpId('department','cb'));
    const courseSelect = document.getElementById(cmrpId('course','cb'));
    
    if (!campusSelect?.value || !termSelect?.value || !departmentSelect?.value || !courseSelect?.value) {
      this.hideCoursesLoading();
      return;
    }

    try {
      // Find campus and term IDs
      const campus = this.config.ddcs_campus_entity.find(c => c.name === campusSelect.value);
      const term = window.courseData.ddcs_term.find(t => t.name === termSelect.value && t.campus_entity_id === campus?.campus_entity_id);
      
      if (!campus || !term) {
        this.hideCoursesLoading();
        return;
      }

      // Fetch all departments for this campus
      const deptQueryData = `fields.campus_entity_id:'${campus.campus_entity_id}' fields.disabled_flag:false`;
      const departments = await this.fetchMetaobjects('ddcs_department', deptQueryData);
      const sortValuesByName = (a, b) => {
        const nameA = a.name;
        const nameB = b.name;

        const isNumA = !isNaN(nameA.charAt(0));
        const isNumB = !isNaN(nameB.charAt(0));
        
        if (isNumA && isNumB) {
          return parseInt(nameA) - parseInt(nameB); 
        }
        
        if (isNumA) {
          return -1; 
        }
        
        if (isNumB) {
          return 1; 
        }
        
        return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
      };
      const sortedValues = departments.sort(sortValuesByName);
      this.allCoursesData = [];

      window.courseData.ddcs_section.forEach(section => {
        const course = window.courseData.ddcs_course.find(c => c.course_id === section.course_id && c.campus_entity_id === section?.campus_entity_id);
        const department = window.courseData.ddcs_campus_department.find(d => d.campus_department_id === course?.campus_department_id && d.campus_entity_id === section?.campus_entity_id);
        if (!course || !department) return;
        this.allCoursesData.push({
          id: `${section.course_id}_${section.section_id}`,
          course_code: course.coursenumber,
          course: course.coursenumber,
          section: section.name,
          section_number: section.name,
          department: department.name,
          division: department.division_name || '',
          course_id: section.course_id,
          section_id: section.section_id,
          department_id: department.campus_department_id
        });
      });
      

      // Render the courses list for mobile
      this.renderAllCoursesList('');
      this.hideCoursesLoading();

    } catch (error) {
      console.error('Error loading courses for mobile:', error);
      this.hideCoursesLoading();
    }
  }

  getAllAvailableCourses() {
    const allCourses = [];
    
    // Get current campus and term IDs
    const campusName = cmrpCourseBuilderCampusTermEl('campus')?.value;
    const termName = cmrpCourseBuilderCampusTermEl('term')?.value;
    
    if (!campusName || !termName) return [];
    
    // Find campus and term IDs
    const normalize = value =>
      String(value).toLowerCase().replace(/\s+/g, '');
    const campus = this.config.ddcs_campus_entity.find(
      c => normalizeDdcsLabel(c.name) === normalizeDdcsLabel(campusName),
    );
    const term = this.config.ddcs_term.find(
      t =>
        normalize(t.term_id) === normalize(termName) &&
        normalize(t.campus_entity_id) === normalize(campus?.campus_entity_id),
    );
    
    if (!campus || !term) return [];
    
    // Get all departments for this campus
    const departments = this.config.ddcs_campus_department.filter(dept => 
      normalize(dept.campus_entity_id) === normalize(campus.campus_entity_id)
    );
    
    // Get all courses and sections for each department
    departments.forEach(department => {
      const courses = this.config.ddcs_course.filter(course => 
        normalize(course.campus_entity_id) === normalize(campus.campus_entity_id) &&
        normalize(course.campus_department_id) === normalize(department.campus_department_id)
      );
      
      courses.forEach(course => {
        const sections = this.config.ddcs_section.filter(section =>
          normalize(section.campus_entity_id) === normalize(campus.campus_entity_id) &&
          normalize(section.term_id) === normalize(term.term_id) &&
          normalize(section.course_id) === normalize(course.course_id)
        );
        
        sections.forEach(section => {
          allCourses.push({
            id: `${course.course_id}_${section.section_id}`,
            course_code: course.coursenumber,
            course: course.coursenumber,
            section: section.name,
            section_number: section.name,
            department: department.name,
            division: department.division_name || '',
            course_id: course.course_id,
            section_id: section.section_id,
            department_id: department.campus_department_id
          });
        });
      });
    });
    
    return allCourses;
  }

  renderAllCoursesList() {
    const coursesList = document.getElementById('all-courses-list');
    const coursesContainer = coursesList?.querySelector('.courses-container');
    
    if (!coursesList || !coursesContainer) return;

    if (this.allCoursesData.length === 0) {
      // Show loading state for a bit longer before showing "no courses" message
      setTimeout(() => {
        coursesContainer.innerHTML = '<div class="no-courses-message">No courses available for this campus and term.</div>';
        coursesList.style.display = 'block';
      }, 1500); // Wait 1.5 seconds before showing the message
      return;
    }

    coursesContainer.innerHTML = '';

    this.allCoursesData.forEach(course => {
      const courseItem = document.createElement('div');
      courseItem.className = `course-item ${this.selectedCoursesFromList.has(course.id) ? 'selected' : ''}`;
      courseItem.dataset.courseId = course.id;
      
      courseItem.innerHTML = `
        <span class="course-item-text">${course.course_code || course.course}, SECTION ${course.section || course.section_number}</span>
        <div class="course-item-action" aria-hidden="true">
          ${this.selectedCoursesFromList.has(course.id) ? 
            '<svg class="check-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" fill="currentColor"/></svg>' :
            '<svg class="plus-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="M8 3.333V12.667M3.333 8H12.667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
          }
        </div>
      `;
      
      courseItem.addEventListener('click', () => this.toggleCourseFromList(course));
      coursesContainer.appendChild(courseItem);
    });

    coursesList.style.display = 'block';
  }

  toggleCourseFromList(course) {
    const courseItem = document.querySelector(`[data-course-id="${course.id}"]`);
    if (!courseItem) return;

    const isSelected = this.selectedCoursesFromList.has(course.id);
    const actionElement = courseItem.querySelector('.course-item-action');
    
    if (isSelected) {
      // Remove the course
      this.selectedCoursesFromList.delete(course.id);
      courseItem.classList.remove('selected');
      
      // Update icon to plus
      if (actionElement) {
        actionElement.innerHTML = '<svg class="plus-icon" width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="M8 3.333V12.667M3.333 8H12.667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      }
      
      // Remove from the form
      this.removeCourseFromList(course);
    } else {
      // Add the course
      this.selectedCoursesFromList.add(course.id);
      courseItem.classList.add('selected');
      
      // Update icon to check
      if (actionElement) {
        actionElement.innerHTML = '<svg class="check-icon" width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" fill="currentColor"/></svg>';
      }
      
      // Add to the form
      this.addCourseFromList(course);
    }
  }

  addCourseFromList(course) {
    // Create course object similar to the dropdown selection
    const courseData = {
      selectedDepartment: course.department || '',
      selectedCampus: cmrpCourseBuilderCampusTermEl('campus')?.value || '',
      selectedTerm: cmrpCourseBuilderCampusTermEl('term')?.value || '',
      selectedcourse: course.course_code || course.course || '',
      selectedsection: course.section || course.section_number || '',
      selectedDivision: course.division || '',
    };

    const objCourse = this.createCourseObject(courseData);
    // Dispatch the same event as the dropdown "Add course" button
    document.dispatchEvent(
      new CustomEvent('sidebar:courseAdded', {
        detail: { course: objCourse },
      })
    );

  }

  removeCourseFromList(course) {
    // Create course object similar to the dropdown selection
    const courseData = {
      selectedDepartment: course.department || '',
      selectedCampus: cmrpCourseBuilderCampusTermEl('campus')?.value || '',
      selectedTerm: cmrpCourseBuilderCampusTermEl('term')?.value || '',
      selectedcourse: course.course_code || course.course || '',
      selectedsection: course.section || course.section_number || '',
      selectedDivision: course.division || '',
    };

    const objCourse = this.createCourseObject(courseData);

    const cmPage =
      window.courseMaterialsPage || window.courseMaterialsPageInstance;
    cmPage?.removeCourseSelection?.(
      objCourse.selectedDepartment,
      objCourse.selectedcourse,
      objCourse.selectedsection,
      objCourse.selectedDivision || '',
    );
  }

  showCoursesLoading() {
    // Try desktop first, then mobile
    let loading = document.getElementById('courses-loading');
    let coursesList = document.getElementById('all-courses-list');
    
    
    if (loading) loading.style.display = 'flex';
    if (coursesList) coursesList.style.display = 'none';
  }

  hideCoursesLoading() {
    // Try desktop first
    let loading = document.getElementById('courses-loading');
    if (loading) loading.style.display = 'none';
  }

  hideAllCoursesList() {
    // Try desktop first
    let coursesList = document.getElementById('all-courses-list');
    if (coursesList) coursesList.style.display = 'none';
  }

  // Course filtering functionality
  filterCourses(event) {
    const searchTerm = event.target.value.toLowerCase().trim();
    this.renderAllCoursesList(searchTerm);
  }

  renderAllCoursesList(searchTerm = '') {
    // Try desktop first, then mobile
    let coursesList = document.getElementById('all-courses-list');
    let coursesContainer = coursesList?.querySelector('.courses-container');
    
    if (!coursesList || !coursesContainer) return;

    if (this.allCoursesData.length === 0) {
      // Show loading state for a bit longer before showing "no courses" message
      setTimeout(() => {
        const messageClass = 'no-courses-message';
        coursesContainer.innerHTML = `<div class="${messageClass}">No courses available for this campus and term.</div>`;
        coursesList.style.display = 'block';
      }, 1500); // Wait 1.5 seconds before showing the message
      return;
    }

    // Filter courses based on search term
    let filteredCourses = this.allCoursesData;
    if (searchTerm) {
      filteredCourses = this.allCoursesData.filter(course => {
        const courseCode = (course.course_code || course.course || '').toLowerCase();
        const section = (course.section || course.section_number || '').toLowerCase();
        return courseCode.includes(searchTerm) || section.includes(searchTerm);
      });
    }

    if (filteredCourses.length === 0) {
      const messageClass = isMobile ? 'mobile-no-courses-message' : 'no-courses-message';
      coursesContainer.innerHTML = `<div class="${messageClass}">No courses found matching your search.</div>`;
      coursesList.style.display = 'block';
      return;
    }

    coursesContainer.innerHTML = '';

    filteredCourses.forEach(course => {
      const courseItem = document.createElement('div');
      const itemClass = isMobile ? 'mobile-course-item' : 'course-item';
      const textClass = isMobile ? 'mobile-course-item-text' : 'course-item-text';
      courseItem.className = `${itemClass} ${this.selectedCoursesFromList.has(course.id) ? 'selected' : ''}`;
      courseItem.dataset.courseId = course.id;
      
      if (isMobile) {
        // Mobile version - simpler layout without action icons
        courseItem.innerHTML = `
          <span class="${textClass}">${course.course_code || course.course}, SECTION ${course.section || course.section_number}</span>
        `;
      } else {
        // Desktop version - with action icons
        courseItem.innerHTML = `
          <span class="${textClass}">${course.course_code || course.course}, SECTION ${course.section || course.section_number}</span>
          <div class="course-item-action" aria-hidden="true">
            ${this.selectedCoursesFromList.has(course.id) ? 
              '<svg class="check-icon" width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" fill="currentColor"/></svg>' :
              '<svg class="plus-icon" width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="M8 3.333V12.667M3.333 8H12.667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
            }
          </div>
        `;
      }
      
      courseItem.addEventListener('click', () => this.toggleCourseFromList(course));
      coursesContainer.appendChild(courseItem);
    });

    coursesList.style.display = 'block';
  }

  /**
   * Toggle mobile drawer visibility
   */
  toggleMobileDrawer() {
    const drawer = document.getElementById('mobile-drawer-section');
    if (!drawer) return;

    const isVisible = drawer.classList.contains('mobile-drawer-open');
    
    if (isVisible) {
      this.closeMobileDrawer();
    } else {
      this.openMobileDrawer();
    }
  }

  /**
   * Open mobile drawer
   */
  openMobileDrawer() {
    this.openMobileDrawerById('mobile-drawer-section');
  }

  /**
   * Open a specific mobile drawer by element id.
   * Defaults to the main course drawer (`mobile-drawer-section`) to preserve existing behavior.
   * @param {string} drawerId
   */
  openMobileDrawerById(drawerId = 'mobile-drawer-section') {
    const drawer = document.getElementById(drawerId);
    const backdrop = document.getElementById('mobile-drawer-backdrop');
    const mobileTrigger = document.getElementById('mobile-course-trigger');

    if (drawer && backdrop) {
      backdrop.classList.add('show');
      drawer.classList.add('mobile-drawer-open');

      if (drawerId === 'mobile-drawer-section' && window.shopifySettings?.studentIDActivation) {
        drawer.classList.add('studentId-Flow-Enabled');
      }

      drawer.style.transform = 'translateY(0)';
      document.body.style.overflow = 'hidden';

      if (drawerId === 'mobile-drawer-section') {
        this.initializeCourseSearch();
        this.checkMobileDrawerEmptyState();
      }

      if (drawerId === 'mobile-drawer-access-program' || drawerId === 'mobile-drawer-requirements') {
        document.body.style.overflow = 'hidden';
        document.documentElement.classList.add('cmrp-scroll-lock');
      }

      if (drawerId === 'mobile-drawer-access-program') {
        backdrop.classList.add('mobile-drawer-backdrop--access');
      } else {
        backdrop.classList.remove('mobile-drawer-backdrop--access');
      }
    }

    if (drawerId === 'mobile-drawer-section' && mobileTrigger) {
      mobileTrigger.style.display = 'none';
    }
  }
  
  // Check and handle empty state for mobile drawer
  checkMobileDrawerEmptyState() {
    window.courseMaterialsMobile?.ensureMobileTagsMatchPageSelection?.();

    const selectedCourses = document.querySelectorAll('.mobile-course-tag');
    const drawerContent = document.querySelector('.mobile-drawer-content');
    const hasMobileTags = selectedCourses && selectedCourses.length > 0;
    const hasPageResults =
      window.courseMaterialsMobile?.hasActiveCourseSelectionOrResults?.() ===
      true;

    if (drawerContent) {
      if (!hasMobileTags && !hasPageResults) {
        drawerContent.classList.add('empty-state');

        if (window.courseMaterialsMobile) {
          window.courseMaterialsMobile.showInitialPlaceholder();
        }
      } else {
        drawerContent.classList.remove('empty-state');

        if (window.courseMaterialsMobile) {
          window.courseMaterialsMobile.hideInitialPlaceholder();
        }
      }
    }
    
    // Update submit button state
    if (window.courseMaterialsMobile) {
      window.courseMaterialsMobile.updateMobileSubmitButtonState();
    }
  }

  /**
   * Initialize course search functionality
   */
  initializeCourseSearch() {
    // Initialize campus/term dropdowns in mobile drawer

    const campusSelect = cmrpCourseBuilderCampusTermEl('campus');
    if (!campusSelect?.value) {
      this.populateCampus();
    }

    const termSelect = cmrpCourseBuilderCampusTermEl('term');
    
    if (campusSelect?.value && termSelect?.value) {
      this.loadAllCourses();
    }
  }

  /**
   * Close mobile drawer
   */
  closeMobileDrawer() {
    const drawer = document.getElementById('mobile-drawer-section');
    const accessDrawer = document.getElementById('mobile-drawer-access-program');
    const requirementsDrawer = document.getElementById('mobile-drawer-requirements');
    const backdrop = document.getElementById('mobile-drawer-backdrop');
    const mobileTrigger = document.getElementById('mobile-course-trigger');

    if (drawer) {
      drawer.classList.remove('mobile-drawer-open');
      drawer.style.transform = 'translateY(100%)';

      const drawerContent = drawer.querySelector('.mobile-drawer-content');
      if (drawerContent) {
        drawerContent.classList.remove('empty-state');
      }
    }

    if (accessDrawer) {
      accessDrawer.classList.remove('mobile-drawer-open');
      accessDrawer.style.transform = 'translateY(100%)';
    }

    if (requirementsDrawer) {
      requirementsDrawer.classList.remove('mobile-drawer-open');
      requirementsDrawer.style.transform = 'translateY(100%)';
    }

    if (backdrop) {
      backdrop.classList.remove('show', 'mobile-drawer-backdrop--access');
    }

    window.courseMaterialsMobile?.syncMobileDrawerBodyScroll?.();

    if (mobileTrigger) {
      mobileTrigger.style.display = 'block';
    }

    document.body.style.overflow = '';
    document.documentElement.classList.remove('cmrp-scroll-lock');
  }

  /**
   * Close Access Program mobile drawer
   */
  closeAccessProgramMobileDrawer() {
    const drawer = document.getElementById('mobile-drawer-access-program');
    const backdrop = document.getElementById('mobile-drawer-backdrop');

    if (drawer && backdrop) {
      drawer.classList.remove('mobile-drawer-open');
      drawer.style.transform = 'translateY(100%)';
      backdrop.classList.remove('show', 'mobile-drawer-backdrop--access');
    }

    document.body.style.overflow = '';
    document.documentElement.classList.remove('cmrp-scroll-lock');

    window.courseMaterialsMobile?.syncMobileDrawerBodyScroll?.();
  }

  /**
   * Initialize mobile drawer on page load - ensure drawer starts closed (no auto-open).
   */
  initializeMobileDrawerOnLoad() {
    if (cmrpIsCourseMaterialsResultsPage()) return;
    if (window.innerWidth <= 968) {
      const drawer = document.getElementById('mobile-drawer-section');
      const backdrop = document.getElementById('mobile-drawer-backdrop');
      
      if (drawer && backdrop) {
        // Ensure drawer starts closed (no mobile-drawer-open on load)
        drawer.classList.remove('mobile-drawer-open');
        drawer.style.transform = 'translateY(100%)';
        backdrop.classList.remove('show');
        
        this.initializeCourseSearch();
      }
    }
  }

  /**
   * Stable chip/product identity: division + department + course + section.
   * Division must be included so the same course/section under different grades stay distinct.
   */
  static courseSelectionKey(department, course, section, division = '') {
    const div = String(division ?? '').trim();
    return `${div}|${department}|${course}|${section}`;
  }

  /**
   * Add course chip. sectionKey must match selectedFilters / URL (option value).
   * sectionLabel is optional display text (option label); defaults to sectionKey.
   * @param {string} [division] optional grade/division display name
   */
  addSelectedCourse(department, course, sectionKey, sectionLabel, division) {
    const container = document.getElementById(
      cmrpId('selected-courses-container', 'cb'),
    );
    if (!container) return;

    const keySection = sectionKey != null && sectionKey !== '' ? String(sectionKey) : '';
    if (!keySection) return;

    const displaySection =
      sectionLabel != null && String(sectionLabel).trim() !== ''
        ? String(sectionLabel).trim()
        : keySection;
    const divisionLabel =
      division != null && String(division).trim() !== ''
        ? String(division).trim()
        : '';

    const courseKey = CourseMaterialsSidebar.courseSelectionKey(
      department,
      course,
      keySection,
      divisionLabel,
    );

    // Check if course already exists
    const existingChip = container.querySelector(`[data-course-key="${CSS.escape(courseKey)}"]`);
    if (existingChip) return;

    const courseChip = document.createElement('div');
    courseChip.className = 'course-chip';
    courseChip.setAttribute('data-course-key', courseKey);
    courseChip.setAttribute('data-division', divisionLabel);
    courseChip.setAttribute('data-department', department);
    courseChip.setAttribute('data-course', course);
    courseChip.setAttribute('data-section', keySection);

    const chipText = document.createElement('span');
    chipText.className = 'course-chip-text';
    const visibleDivision = decodeHtmlEntities(divisionLabel);
    const visibleDepartment = decodeHtmlEntities(department);
    const visibleCourse = decodeHtmlEntities(course);
    const visibleSection = decodeHtmlEntities(displaySection);
    chipText.textContent = visibleDivision
      ? `${visibleDivision} | ${visibleDepartment} | ${visibleCourse} | ${visibleSection}`
      : `${visibleDepartment} | ${visibleCourse} | ${visibleSection}`;

    const removeEl = document.createElement('div');
    removeEl.className = 'course-chip-remove';
    removeEl.setAttribute('role', 'button');
    removeEl.setAttribute('tabindex', '0');
    removeEl.setAttribute('aria-label', 'Remove course');
    removeEl.textContent = '×';
    const onRemove = e => {
      e.preventDefault();
      e.stopPropagation();
      const cmPage =
        window.courseMaterialsPage || window.courseMaterialsPageInstance;
      cmPage?.removeCourseSelection?.(
        department,
        course,
        keySection,
        divisionLabel,
      );
    };
    removeEl.addEventListener('click', onRemove);
    removeEl.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') onRemove(e);
    });

    courseChip.appendChild(chipText);
    courseChip.appendChild(removeEl);

    container.appendChild(courseChip);
  }

  /**
   * Remove desktop bubbles and mobile tags for one course (trimmed attribute match).
   * @param {string} department
   * @param {string} course
   * @param {string} section
   * @param {string} [division]
   */
  clearCourseSelectionUI(department, course, section, division) {
    const t = v =>
      v == null || v === '' ? '' : String(v).trim();
    const looseEq = (a, b) => {
      const n = v =>
        String(v ?? '')
          .trim()
          .toLowerCase()
          .replace(/[\s_-]+/g, '');
      return n(a) === n(b);
    };
    const d = t(department);
    const c = t(course);
    const s = t(section);
    const divProvided = division !== undefined && division !== null;
    const div = divProvided ? t(division) : null;
    if (!d || !c || !s) return;

    const match = el => {
      const ed = el.getAttribute('data-department');
      const ec = el.getAttribute('data-course');
      const es = el.getAttribute('data-section');
      const ediv = el.getAttribute('data-division') || '';
      const exact =
        t(ed) === d && t(ec) === c && t(es) === s;
      const loose =
        looseEq(ed, d) && looseEq(ec, c) && looseEq(es, s);
      if (!(exact || loose)) return false;
      if (!divProvided) return true;
      return exact ? t(ediv) === div : looseEq(ediv, div);
    };

    [
      document.getElementById(cmrpId('selected-courses-container', 'cb')),
      document.getElementById('mobile-selected-courses'),
    ].forEach(root => {
      if (!root) return;
      root
        .querySelectorAll('.course-chip, .mobile-course-tag')
        .forEach(chip => {
          if (match(chip)) chip.remove();
        });
    });

    const mobileWrap = document.getElementById('mobile-selected-courses');
    if (
      mobileWrap &&
      !mobileWrap.querySelector('.mobile-course-tag')
    ) {
      mobileWrap.style.display = 'none';
    }
  }

  // Get all selected courses
  getSelectedCourses() {
    const chips = document.querySelectorAll('.course-chip');
    return Array.from(chips).map(chip => ({
      division: chip.getAttribute('data-division'),
      department: chip.getAttribute('data-department'),
      course: chip.getAttribute('data-course'),
      section: chip.getAttribute('data-section'),
      key: chip.getAttribute('data-course-key')
    }));
  }

  /**
   * Sync course bubbles from filter data
   * Called when products are displayed to ensure bubbles match the rendered courses.
   * Rebuilds the desktop chip list each time (add-only sync left stale course-builder chips
   * after a student-ID lookup replaced selectedFilters). Only shows course-builder rows
   * (not fromStudentId), matching mobile #mobile-selected-courses behavior.
   * @param {Array} filters - Array of filter objects with selectedDepartment, selectedcourse, selectedsection
   */
  syncBubblesFromFilters(filters) {
    const container = document.getElementById(
      cmrpId('selected-courses-container', 'cb'),
    );
    if (!container) {
      if (filters && Array.isArray(filters) && filters.length > 0) {
        setTimeout(() => this.syncBubblesFromFilters(filters), 500);
      }
      return;
    }

    if (!filters || !Array.isArray(filters) || filters.length === 0) {
      container.innerHTML = '';
      if (
        window.courseMaterialsMobile &&
        typeof window.courseMaterialsMobile.syncMobileTagsFromFilters === 'function'
      ) {
        window.courseMaterialsMobile.syncMobileTagsFromFilters([]);
      }
      return;
    }

    container.innerHTML = '';

    filters.forEach(filter => {
      if (
        !filter ||
        filter.fromStudentId ||
        !filter.selectedDepartment ||
        !filter.selectedcourse ||
        !filter.selectedsection
      ) {
        return;
      }
      const department = filter.selectedDepartment;
      const course = filter.selectedcourse;
      const section = filter.selectedsection;
      const division = filter.selectedDivision || '';
      this.addSelectedCourse(department, course, section, section, division);
    });

    if (
      window.courseMaterialsMobile &&
      typeof window.courseMaterialsMobile.syncMobileTagsFromFilters === 'function'
    ) {
      window.courseMaterialsMobile.syncMobileTagsFromFilters(filters);
    }
  }
}

window.CourseMaterialsSidebar = CourseMaterialsSidebar;

/** CMRP desktop sidebar breakpoint (matches max-width: 968px mobile rules). */
const CMRP_DESKTOP_SIDEBAR_MQ = '(min-width: 969px)';

/**
 * Reveal course-builder "Make your selection…" copy, then clear data-cmrp-flow-pending.
 * That prompt belongs on the course-builder form itself — including stores where
 * shop-by-student-id is off (unsuffixed heading id, no student-id-mode). The whole
 * course-builder form is already hidden during student-id flow, so the heading does
 * not need its own student-id-mode toggle (leftover from dual-form layout).
 */
function applyCmrpCourseDetailsSectionFlowUi() {
  const filtersContent = document.querySelector('.filters-content');
  const filters = document.getElementById('filters-section');

  // Student-id-off stores use unsuffixed ids, so cmrpId(..., 'sid') resolves to
  // #cmrp-course-details-heading (the course-builder prompt). Never hide via that lookup.
  document
    .querySelectorAll(
      '.cmrp-filter-form--course-builder .cmrp-course-details-heading',
    )
    .forEach(headingCb => {
      headingCb.removeAttribute('hidden');
    });

  if (filters && cmrpStudentIdThemeEnabled()) {
    const isStudentFlow = filters.classList.contains('student-id-mode');
    filters.classList.toggle('cmrp-filters-panel--landing-chrome', !isStudentFlow);
    const productsSelected = document.querySelector(
      '#products-section > .selected-courses-section.buy-by-course-builder__selected-wrap',
    );
    if (productsSelected) {
      productsSelected.hidden = isStudentFlow;
    }
  }

  filtersContent?.removeAttribute('data-cmrp-flow-pending');
}

window.applyCmrpCourseDetailsSectionFlowUi = applyCmrpCourseDetailsSectionFlowUi;

if (typeof window !== 'undefined' && typeof window.matchMedia !== 'undefined') {
  const cmrpDesktopSidebarMq = window.matchMedia(CMRP_DESKTOP_SIDEBAR_MQ);
  const onCmrpDesktopSidebarMqChange = () => applyCmrpCourseDetailsSectionFlowUi();
  if (typeof cmrpDesktopSidebarMq.addEventListener === 'function') {
    cmrpDesktopSidebarMq.addEventListener('change', onCmrpDesktopSidebarMqChange);
  } else if (typeof cmrpDesktopSidebarMq.addListener === 'function') {
    cmrpDesktopSidebarMq.addListener(onCmrpDesktopSidebarMqChange);
  }
}

function consumeCmrpStudentIdPrefill() {
  try {
    const raw = sessionStorage.getItem(CMRP_STUDENT_ID_PREFILL_KEY);
    if (!raw) return '';
    sessionStorage.removeItem(CMRP_STUDENT_ID_PREFILL_KEY);
    return String(raw).trim();
  } catch (e) {
    return '';
  }
}

function applyCmrpStudentIdPrefillToInputs() {
  const v = consumeCmrpStudentIdPrefill();
  if (!v) return;
  const d = document.getElementById(cmrpId('student-id','sid'));
  const m = document.getElementById('mobile-student-id-input');
  if (d) d.value = v;
  if (m) m.value = v;
}

window.CMRP_STUDENT_ID_PREFILL_KEY = CMRP_STUDENT_ID_PREFILL_KEY;
window.consumeCmrpStudentIdPrefill = consumeCmrpStudentIdPrefill;
window.applyCmrpStudentIdPrefillToInputs = applyCmrpStudentIdPrefillToInputs;

async function cmrpRecoverSessionTokenFromCart() {
  try {
    const existing = sessionStorage.getItem('sessionToken');
    if (existing) return existing;
    const CART_TIMEOUT_MS = 8000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CART_TIMEOUT_MS);
    try {
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        signal: controller.signal,
      });
      if (!res.ok) return '';
      const cart = await res.json();
      const token = cart?.attributes?.sessionToken;
      if (!token || typeof token !== 'string') return '';
      sessionStorage.setItem('sessionToken', token);
      return token;
    } finally {
      clearTimeout(timeoutId);
    }
  } catch (error) {
    console.error('Error recovering session token from cart:', error);
    return '';
  }
}

function cmrpPersistSessionStudentId(studentId) {
  try {
    const trimmed = String(studentId || '').trim();
    if (!trimmed) return;
    sessionStorage.setItem(CMRP_SESSION_STUDENT_ID_KEY, trimmed);
  } catch (e) {
    /* ignore */
  }
}

function cmrpClearSessionStudentId() {
  try {
    sessionStorage.removeItem(CMRP_SESSION_STUDENT_ID_KEY);
  } catch (e) {
    /* ignore */
  }
}

function cmrpApplySessionStudentIdToInputs(studentId) {
  const trimmed = String(studentId || '').trim();
  if (!trimmed) return;
  const desktop = document.getElementById(cmrpId('student-id', 'sid'));
  const mobile = document.getElementById('mobile-student-id-input');
  if (desktop) desktop.value = trimmed;
  if (mobile) mobile.value = trimmed;
}

async function cmrpGlobalCheckSession() {
  await cmrpRecoverSessionTokenFromCart();
  const token = sessionStorage.getItem('sessionToken');
  if (!token) return false;

  const SESSION_CHECK_TIMEOUT_MS = 8000;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SESSION_CHECK_TIMEOUT_MS);
  try {
    const response = await fetch('/apps/shopifyData/session/check', {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        return { status: false };
      }
      return undefined;
    }
    const data = await response.json();
    if (data?.status && data.sessionID) {
      cmrpPersistSessionStudentId(data.sessionID);
    }
    return data;
  } catch (error) {
    if (error && error.name === 'AbortError') {
      console.error(
        `[CMRP] session/check timed out after ${SESSION_CHECK_TIMEOUT_MS}ms`
      );
      return undefined;
    }
    console.error('Error checking session:', error);
    return undefined;
  } finally {
    clearTimeout(timeoutId);
  }
}

window.cmrpRecoverSessionTokenFromCart = cmrpRecoverSessionTokenFromCart;
window.cmrpPersistSessionStudentId = cmrpPersistSessionStudentId;
window.cmrpClearSessionStudentId = cmrpClearSessionStudentId;
window.cmrpApplySessionStudentIdToInputs = cmrpApplySessionStudentIdToInputs;
window.cmrpGlobalCheckSession = cmrpGlobalCheckSession;

function reorderStudentIdFlowDesktopLayout() {
  if (
    !window.matchMedia ||
    (!window.matchMedia('(min-width: 969px)').matches &&
      !cmrpIsCourseMaterialsResultsPage())
  ) {
    return;
  }
  const sidRoot = document.querySelector('.cmrp-filter-form--student-id');
  if (!sidRoot) return;
  const glassLayout = sidRoot.classList.contains('cmrp-student-id-layout--glass');
  const courseDetailsSection = sidRoot.querySelector('.course-details-section');
  const student_id_section = sidRoot.querySelector('.student-id-section');
  const desktopSubmitContainer = sidRoot.querySelector('.student-id-submit-container-desktop');
  const submitStudentIdButton = document.getElementById(
    cmrpId('submit-student-id', 'sid'),
  );
  if (!glassLayout && courseDetailsSection && student_id_section) {
    sidRoot.insertBefore(student_id_section, courseDetailsSection);
  }
  if (desktopSubmitContainer && submitStudentIdButton) {
    if (!desktopSubmitContainer.contains(submitStudentIdButton)) {
      desktopSubmitContainer.appendChild(submitStudentIdButton);
    }
    desktopSubmitContainer.style.display = 'flex';
  }
  anchorCmrpProductsPanel();
}

function cmrpHasDeepCourseUrlParams() {
  return cmrpUrlHasCourseRestoreData();
}

async function refreshStudentIdSessionUI() {
  const searchParams = new URLSearchParams(window.location.search);
  const hashValue = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const hashParams = new URLSearchParams(hashValue);
  const hasCodeParam =
    Boolean(searchParams.get('code')) || Boolean(hashParams.get('code'));

  const courseLineFields = cmrpCourseBuilderCourseLineFieldEls();
  const dualForms = cmrpShowBothForms();
  if (courseLineFields.length && !dualForms) {
    courseLineFields.forEach(el => {
      el.style.display = 'none';
    });
  }
  const session_check = await cmrpGlobalCheckSession();

  if (session_check && session_check.status && session_check.sessionID) {
    cmrpPersistSessionStudentId(session_check.sessionID);
    const filtersSectionEarly = document.getElementById('filters-section');
    if (filtersSectionEarly) filtersSectionEarly.classList.add('student-id-mode');
    const studentIdInfoEarly = document.getElementById(
      cmrpId('student-id-info-desktop', 'sid'),
    );
    if (
      studentIdInfoEarly &&
      window.matchMedia &&
      (window.matchMedia('(min-width: 768px)').matches ||
        cmrpIsCourseMaterialsResultsPage())
    ) {
      studentIdInfoEarly.style.display = 'flex';
    }
    reorderStudentIdFlowDesktopLayout();
    const student_id_section = document.querySelector(
      '.cmrp-filter-form--student-id .student-id-section',
    );
    applyCmrpTypedStudentIdSectionVisibility(student_id_section, { dualForms });
    cmrpApplySessionStudentIdToInputs(session_check.sessionID);
    if (courseLineFields.length && !dualForms) {
      courseLineFields.forEach(el => {
        el.style.display = 'none';
      });
    }
  } else if (session_check && session_check.status === false) {
    sessionStorage.removeItem('sessionToken');
    cmrpClearSessionStudentId();
    const student_id_section = document.querySelector(
      '.cmrp-filter-form--student-id .student-id-section',
    );
    applyCmrpTypedStudentIdSectionVisibility(student_id_section, { dualForms });
    if (courseLineFields.length) {
      courseLineFields.forEach(el => {
        el.style.display = dualForms ? '' : hasCodeParam ? 'none' : '';
      });
    }
  } else {
    const student_id_section = document.querySelector(
      '.cmrp-filter-form--student-id .student-id-section',
    );
    applyCmrpTypedStudentIdSectionVisibility(student_id_section, { dualForms });
    if (courseLineFields.length) {
      courseLineFields.forEach(el => {
        el.style.display = dualForms ? '' : hasCodeParam ? 'none' : '';
      });
    }
  }
  anchorCmrpProductsPanel();
  window.syncCmrpSsoLandingPanelFromSession?.();
  syncCmrpTypedStudentIdDomFlags();
}

window.refreshStudentIdSessionUI = refreshStudentIdSessionUI;
window.syncCmrpTypedStudentIdDomFlags = syncCmrpTypedStudentIdDomFlags;
window.syncCmrpSsoLandingPanelFromSession = function syncCmrpSsoLandingPanelFromSession() {
  const sb = window.courseMaterialsSidebar;
  if (sb && typeof sb.syncCmrpSsoLandingPanelFromSession === 'function') {
    sb.syncCmrpSsoLandingPanelFromSession();
  } else {
    syncCmrpTypedStudentIdDomFlags();
  }
};
window.reorderStudentIdFlowDesktopLayout = reorderStudentIdFlowDesktopLayout;

document.addEventListener('DOMContentLoaded', async () => {
  await cmrpRecoverSessionTokenFromCart();
  syncCmrpTypedStudentIdDomFlags();
  cmrpAdoptFlowFromShopByIfNeeded();
  cmrpAttachCmrpSelectTruncationListeners();
  cmrpInstallCmrpSelectTruncationMutationObserver();
  cmrpInstallCmrpSelectTruncateResizeObserver();
  requestAnimationFrame(() => {
    window.cmrpTruncateCmrpFilterSelectsIn?.(
      document.getElementById('filters-section'),
    );
  });

  document.addEventListener(
    'product-display:updated',
    () => {
      requestAnimationFrame(() =>
        window.cmrpTruncateCmrpFilterSelectsIn?.(
          document.getElementById('filters-section'),
        ),
      );
    },
    { passive: true },
  );

  const themeStudentId =
    document.getElementById('filters-section')?.dataset?.studentIdActivation ===
    'true';
  if (themeStudentId) {
    (function ensureDefaultCmrpFlowFromTheme() {
      if (cmrpAdoptFlowFromShopByIfNeeded()) return;
      if (cmrpUrlShouldRestoreAsCourseBuilder()) return;
      if (cmrpCurrentFlow()) return;
      cmrpPersistFlow('student-id');
      if (typeof window.setCmrpInitialFlowPreference === 'function') {
        window.setCmrpInitialFlowPreference('student-id');
      }
    })();
  }

  const forcedFlow = cmrpCurrentFlow();
  const filtersSection = document.getElementById('filters-section');

  if (forcedFlow === 'student-id' || forcedFlow === 'sso') {
    const student_id_section = document.querySelector(
      '.cmrp-filter-form--student-id .student-id-section',
    );
    applyCmrpTypedStudentIdSectionVisibility(student_id_section, {
      dualForms: cmrpShowBothForms(),
    });
    const studentIdInfoDesktop = document.getElementById(
      cmrpId('student-id-info-desktop', 'sid'),
    );
    if (
      studentIdInfoDesktop &&
      window.matchMedia &&
      (window.matchMedia('(min-width: 969px)').matches ||
        cmrpIsCourseMaterialsResultsPage())
    ) {
      studentIdInfoDesktop.style.display = 'flex';
    }
    if (filtersSection) filtersSection.classList.add('student-id-mode');

    if (
      window.matchMedia &&
      (window.matchMedia('(min-width: 969px)').matches ||
        cmrpIsCourseMaterialsResultsPage())
    ) {
      reorderStudentIdFlowDesktopLayout();
    }
    window.applyCmrpCourseDetailsSectionFlowUi?.();
    const session_check = await cmrpGlobalCheckSession();
    if (session_check && session_check.status && session_check.sessionID) {
      cmrpPersistSessionStudentId(session_check.sessionID);
      cmrpApplySessionStudentIdToInputs(session_check.sessionID);
      try {
        sessionStorage.removeItem('cmrp_student_id_prefill');
      } catch (e) {}
    } else if (typeof applyCmrpStudentIdPrefillToInputs === 'function') {
      applyCmrpStudentIdPrefillToInputs();
    } else {
      (function applyCmrpStudentIdPrefillFallback() {
        try {
          const k = 'cmrp_student_id_prefill';
          const v = sessionStorage.getItem(k);
          if (!v) return;
          sessionStorage.removeItem(k);
          const t = String(v).trim();
          if (!t) return;
          const d = document.getElementById(cmrpId('student-id', 'sid'));
          const m = document.getElementById('mobile-student-id-input');
          if (d) d.value = t;
          if (m) m.value = t;
        } catch (e) {}
      })();
    }
    scheduleCmrpAnchorProductsPanel();
    window.syncCmrpSsoLandingPanelFromSession?.();
    return;
  }

  if (forcedFlow === 'course-builder') {
    const reclaimStudentIdFlow =
      cmrpStudentIdThemeEnabled() &&
      readCmrpInitialFlowPreference() !== 'course-builder' &&
      !cmrpUrlPrefersCourseBuilderDeepLink() &&
      !cmrpShopByIsStudentId(cmrpReadShopByFromSearch());
    if (reclaimStudentIdFlow) {
      const session_check = await cmrpGlobalCheckSession();
      if (session_check?.status && session_check.sessionID) {
        cmrpPersistFlow('student-id');
        if (filtersSection) filtersSection.classList.add('student-id-mode');
        window.applyCmrpCourseDetailsSectionFlowUi?.();
        await refreshStudentIdSessionUI();
        scheduleCmrpAnchorProductsPanel();
        window.syncCmrpSsoLandingPanelFromSession?.();
        return;
      }
    }
    cmrpApplyFlowChrome('course-builder');
    window.applyCmrpCourseDetailsSectionFlowUi?.();
    scheduleCmrpAnchorProductsPanel();
    window.syncCmrpSsoLandingPanelFromSession?.();
    return;
  }

  await refreshStudentIdSessionUI();
  const session_check = await cmrpGlobalCheckSession();
  if (filtersSection) {
    if (session_check && session_check.status && session_check.sessionID) {
      cmrpPersistFlow('student-id');
      filtersSection.classList.add('student-id-mode');
      cmrpApplySessionStudentIdToInputs(session_check.sessionID);
      const studentIdInfoDesktop = document.getElementById(
        cmrpId('student-id-info-desktop', 'sid'),
      );
      if (
        studentIdInfoDesktop &&
        window.matchMedia &&
        (window.matchMedia('(min-width: 969px)').matches ||
          cmrpIsCourseMaterialsResultsPage())
      ) {
        studentIdInfoDesktop.style.display = 'flex';
      }
      const student_id_section = document.querySelector(
        '.cmrp-filter-form--student-id .student-id-section',
      );
      applyCmrpTypedStudentIdSectionVisibility(student_id_section, {
        dualForms: cmrpShowBothForms(),
      });
      if (
        student_id_section &&
        window.matchMedia &&
        (window.matchMedia('(min-width: 969px)').matches ||
          cmrpIsCourseMaterialsResultsPage())
      ) {
        reorderStudentIdFlowDesktopLayout();
      }
    } else if (!sessionStorage.getItem('sessionToken')) {
      cmrpPersistFlow('course-builder');
      filtersSection.classList.remove('student-id-mode');
    }
  }
  window.applyCmrpCourseDetailsSectionFlowUi?.();
  scheduleCmrpAnchorProductsPanel();
  window.syncCmrpSsoLandingPanelFromSession?.();
});

let cmrpAnchorProductsResizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(cmrpAnchorProductsResizeTimer);
  cmrpAnchorProductsResizeTimer = setTimeout(() => {
    anchorCmrpProductsPanel();
    window.cmrpTruncateCmrpFilterSelectsIn?.(document.getElementById('filters-section'));
  }, 120);
});

if (typeof window.visualViewport !== 'undefined' && window.visualViewport) {
  window.visualViewport.addEventListener('resize', () => {
    clearTimeout(cmrpAnchorProductsResizeTimer);
    cmrpAnchorProductsResizeTimer = setTimeout(() => {
      window.cmrpTruncateCmrpFilterSelectsIn?.(document.getElementById('filters-section'));
    }, 120);
  });
}

document.addEventListener('sidebar:courseAdded', () => {
  requestAnimationFrame(() => anchorCmrpProductsPanel());
});
document.addEventListener('sidebar:studentSubmitted', () => {
  requestAnimationFrame(() => anchorCmrpProductsPanel());
});

// Export for global use
window.CourseMaterialsSidebar = CourseMaterialsSidebar;
